import {
  DO_NOT_USE_OR_YOU_WILL_BE_FIRED_EXPERIMENTAL_FORM_ACTIONS,
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Link, useLocation } from "react-router-dom"
import {
  COLOR_BG_GRAY,
  parsePathForEntityUUID,
  pbUUIDToUuid,
  uuidToPbUUID,
  formatDayOrLongDash,
} from "../utils/utils"
import { Translated } from "../utils/translated"
import {
  Annex,
  Equipment,
  Impact,
  MethodTypeEN1177,
  NormType,
  Site,
  UUID,
  Zone,
} from "../generated/proto-ts/main"
import { Col, Divider, FloatButton, Popover, Row, Spin, Typography } from "antd"
import {
  EResultTagAdequacy,
  EResultTagTL,
  FlexCol,
  FlexRow,
  RequirementsAlertSM,
} from "../components/commons-ts/common"
import { callCloudApiV2 } from "../utils/cloudApiV2"
import {
  ECloudDeviceEventType,
  ICloudCustomer,
  ICloudDevice,
  ICloudDeviceEvent,
  ICloudUser,
  ICoordinate,
  IImpactPoints,
  MeanWithStd,
} from "../types"
import {
  FilePdfOutlined,
  GlobalOutlined,
  LeftCircleOutlined,
  SignatureOutlined,
} from "@ant-design/icons"
import { sportsMatTypeDescriptionStr } from "../components/commons-ts/tags"
import { getEquipmentSportsMatThicknessMeanAndStd } from "./02_Equipment"

import { getRequirementsSM, getSiteResultSM, getZoneResultSM } from "../calculus/calculus_SM"
import {
  EquipmentZonesTableSM_Print,
  IZoneProcessed_SM,
} from "./Tables/02_EquipmentZones/EquipmentZonesTable_SM"
import { ReportConfigProvider, useReportConfigContext } from "../providers/reportConfigProvider"
import { ImageUploader } from "../components/report-ts/reportImageUploader"
import {
  PDFInputTextArea,
  PDFInput,
  PDFHidableSpan,
  PDFControlledHidableSpan,
  PDFControlledInput,
} from "../components/report-ts/reportInputs"
import { FirstPicture } from "../components/report-ts/reportPicturesRow"
import {
  useUsercommImpactBimodal,
  useUsercommSiteAnnexesBimodal,
  useUsercommSiteBimodal,
  useUsercommSiteChildrenRecursiveBimodal,
} from "../usercomm/common/usercommAsyncRequestBimodal"
import { FOOTER_HEIGHT, HEADER_HEIGHT } from "../components/report-ts"
import { useTranslationContext } from "../providers/translationProvider"
import { PDFEmptySpacePlaceholder } from "../components/report-ts/reportEmptySpacePlaceholder"
import { SiteEquipmentsTableMetadataPF_Print } from "./Tables/01_SiteEquipments/SiteEquipmentsTable_PF"
import { SiteEnvironmentalConditionsTable_PF } from "./Tables/00_SiteEnvironmentalConditions/00_SiteEnvironmentalConditionsTable_PF"

import dayjs from "dayjs"
import {
  EquipmentZonesTablePF_ADQ_Print,
  EquipmentZonesTablePF_CFH_Print,
  IZoneProcessed_ADQ,
  IZoneProcessed_CFH,
} from "./Tables/02_EquipmentZones/EquipmentZonesTable_PF"
import {
  EquipmentZonesTableTL_Print,
  IZoneProcessed_TL,
} from "./Tables/02_EquipmentZones/EquipmentZonesTable_TL"
import {
  ZoneImpactsTablePF_ADQ_Print,
  ZoneImpactsTablePF_CFH_Print,
} from "./Tables/03_ZoneImpacts/ZoneImpactsTable_PF"
import {
  ImpactChartKinematicAcceleration,
  ImpactChartKinematicDistance_Deflection,
  ImpactChartKinematicDistance_SecondarySuspense,
  ImpactChartKinematicVelocity_Resilience,
  ImpactChartKinematicVelocity_SecondarySuspense,
  ImpactChartRawAcceleration,
  ZoneChartPF_CFH,
} from "../components/commons-ts/uplotCharts"
import { decodeImpactDataPoints } from "../usercomm/common/usercommUtils"
import { ZoneChartPF_ADQ } from "../components/commons-ts/simpleCharts"
import { StatEnvelope } from "../utils/statEnvelope"
import {
  getCFH_Global,
  getSiteResultPF_Adq,
  getSiteResultPF_CFH,
  getZoneResultPF_Adq,
  getZoneResultPF_CFH,
} from "../calculus/calculus_PF"
import { TranslationLanguageSelectWidget } from "../components/commons-ts/translationLanguageSelectWidget"
import { EResult } from "../calculus/types"
import { decimalCoordinateToDegreesMinutesSeconds } from "./Settings/GNSSUbloxSettings"
import { getImpactKinematics } from "../calculus/kinematics"
import { ZoneImpactsTableSM_Print } from "./Tables/03_ZoneImpacts/ZoneImpactsTable_SM"
import { SiteEquipmentsTableMetadataSM_Print } from "./Tables/01_SiteEquipments/SiteEquipmentsTable_SM"
import { GoogleAddressWidget, LabeledTsCoordinate } from "../components/commons-ts/googleMaps"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExternalLink } from "@fortawesome/free-solid-svg-icons"
import {
  _calculatePerformanceFactor,
  getSiteResultTL,
  getZoneResultTL,
} from "../calculus/calculus_TL"
import { ZoneImpactsTableTL_Print } from "./Tables/03_ZoneImpacts/ZoneImpactsTable_TL"
import { SiteEquipmentsTableMetadataTL_Print } from "./Tables/01_SiteEquipments/SiteEquipmentsTable_TL"

const PDF_INPUT_TEXT_AREA_STYLE = {
  fontSize: 14,
}

const PDF_PAGE_CONTENT_MARGIN_TOP = 10

const PDF_EQUIPMENT_SUMMARY_EXPANDED_MODE_COUNT = 4
const PDF_EQUIPMENT_SUMMARY_SUPEREXPANDED_MODE_COUNT = 8
const PDF_EQUIPMENT_ZONES_MAX_SINGLE_PAGE_COUNT = 6

enum PDFPrimaryPagesMode {
  CompactMode = 0, // Page 2: introduction, identification (table <= 4), methodology, reference equipment; Page 3: environment, geolocation, comment
  ExpandedMode = 1, // Page 2: introduction, identification (4 < table <= 8), methodology; Page 3: reference equipment, environment; Page 4: geolocation, comment
  SuperExpandedMode = 2, // Page 2: introduction, identification (table > 8); Page 3: methodology, reference equipment, geolocation; Page 4: environment, comment
}

enum PDFEquipmentPageMode {
  SinglePage = 0, // Equipment's zones table is displayed in its entirety on a single page
  MultiPage = 1, // Equipment's zones table is split into several pages
}

const normTypeString = (normType: NormType): string => {
  let normTypeStr = ""
  switch (normType) {
    case NormType.EN_1177:
      normTypeStr = "EN 1177:2018+A1:2023"
      break
    case NormType.EN_12503:
      normTypeStr = "EN 12503"
      break
    case NormType.EN_14960:
      normTypeStr = "EN 14960"
      break
    case NormType.EN_ISO_23659:
      normTypeStr = "EN ISO 23659"
      break
    case NormType.EN_1176:
      normTypeStr = "EN 1176"
      break
    default:
      normTypeStr = ""
      break
  }
  return normTypeStr
}

const PDFHeader: FC<{
  site: Site | null
  user: ICloudUser | null
}> = ({ site, user }) => {
  const {
    leftDataURL,
    rightHeaderReferenceNote,
    rightHeaderDateNote,
    rightHeaderVersionNote,
    rightHeaderVersionSubscriptIsVisible,
    setLeftDataURL,
    setRightHeaderReferenceNote,
    setRightHeaderDateNote,
    setRightHeaderVersionNote,
    setRightHeaderVersionSubscriptIsVisible,
  } = useReportConfigContext()

  useEffect(() => {
    if (!site) {
      return
    }
    let ref = site.mission_name + " " + site.client_name + " " + site.site_name
    setRightHeaderReferenceNote(ref)
  }, [site])

  useEffect(() => {
    if (site === null) {
      return
    }
    let normTypeStr = normTypeString(site.norm_type)
    let methodTypeStr = ""
    switch (site.method_type_en_1177) {
      case MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION:
        methodTypeStr = "1-CFH"
        break
      case MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE:
        methodTypeStr = "2-ADQ"
        break
    }

    let documentName = `${rightHeaderDateNote}_${normTypeStr}_${methodTypeStr}_${rightHeaderReferenceNote}_v${rightHeaderVersionNote}`
    document.title = window.parent.document.title = documentName
  }, [site, rightHeaderDateNote, rightHeaderReferenceNote, rightHeaderVersionNote])

  useEffect(() => {
    setRightHeaderDateNote(dayjs().format("YYYY-MM-DD"))
    setRightHeaderVersionNote("1.0")
    setRightHeaderVersionSubscriptIsVisible(true)
  }, [])

  const memoEnterpriseLogoDataUrl = useMemo(() => {
    if (!user) {
      return null
    }
    if (user.Enterprise === null || user.Enterprise.EnterprisePictureUUID === null) {
      return null
    }
    return `/api/uploads/${user.Enterprise.EnterprisePictureUUID}`
  }, [user])

  return (
    <header
      style={{
        borderBottom: `3px solid #ccc`,
        fontSize: "0.9rem",
        textAlign: "center",
      }}
    >
      <FlexRow
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          height: HEADER_HEIGHT,
          marginBottom: 5,
        }}
      >
        <ImageUploader
          dataURL={memoEnterpriseLogoDataUrl ?? leftDataURL}
          setDataURL={setLeftDataURL}
        />
        <FlexCol
          style={{
            alignItems: "end",
            gap: 0,
            width: "100%",
          }}
        >
          <span>
            <b>
              <PDFControlledInput
                value={rightHeaderReferenceNote ?? ""}
                setValue={(v) => setRightHeaderReferenceNote(v?.toString() ?? "")}
                inputProps={{
                  style: { width: "fit-content", textAlign: "end" },
                }}
              />
            </b>
          </span>
          <span>
            <Translated keyEn="Edited on" />:{" "}
            <b>
              <PDFControlledInput
                value={rightHeaderDateNote ?? ""}
                setValue={(v) => setRightHeaderDateNote(v?.toString() ?? "")}
                inputProps={{
                  style: { width: "fit-content", textAlign: "end" },
                }}
              />
            </b>
          </span>
          <span>
            <Translated keyEn="Version" />:{" "}
            <b>
              <PDFControlledInput
                value={rightHeaderVersionNote ?? ""}
                setValue={(v) => setRightHeaderVersionNote(v?.toString() ?? "")}
                inputProps={{
                  style: { width: "fit-content", textAlign: "end" },
                }}
              />
            </b>
          </span>
          <span style={{ fontSize: "0.7rem" }}>
            <i>
              <PDFControlledHidableSpan
                isHidden={!rightHeaderVersionSubscriptIsVisible}
                setIsHidden={(isHidden) => setRightHeaderVersionSubscriptIsVisible(!isHidden)}
              >
                <Translated keyEn="This version replaces and cancels all the previous ones" />
              </PDFControlledHidableSpan>
            </i>
          </span>
        </FlexCol>
      </FlexRow>
    </header>
  )
}

const PDFFootnoteTextArea: FC<{
  footnote: string | null
  setFootnote: (footnote: string | null) => void
  textAlign: "center" | "left" | "right"
}> = ({ footnote, setFootnote, textAlign }) => {
  return (
    <span
      style={{
        textAlign: "center",
        width: "100%",
      }}
    >
      <PDFInputTextArea
        style={{
          textAlign,
        }}
        autoSize={{
          minRows: 2,
          maxRows: 2,
        }}
        value={footnote ?? ""}
        onChange={(e) => {
          setFootnote(e.target.value)
        }}
      />
    </span>
  )
}

const PDFFooter: FC<{
  pageNumber: number
  totalPages: number
}> = ({ pageNumber, totalPages }) => {
  const { translated } = useTranslationContext()
  const { leftFootnote, rightFootnote, setLeftFootnote, setRightFootnote } =
    useReportConfigContext()

  useEffect(() => {
    if (rightFootnote === "") {
      setRightFootnote(
        translated(
          "Reproduction of this inspection report is only permitted in the form of a complete photographic facsimile",
        ),
      )
    }
  }, [])

  return (
    <footer
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        marginLeft: "8mm",
        marginRight: "8mm",
        width: "calc(100% - 16mm)",
        height: FOOTER_HEIGHT,
        borderTop: `3px solid #ccc`,
        paddingTop: 5,
      }}
    >
      <FlexRow
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.7rem",
        }}
      >
        <PDFFootnoteTextArea
          footnote={leftFootnote}
          setFootnote={setLeftFootnote}
          textAlign="left"
        />
        <p style={{ width: "fit-content", whiteSpace: "nowrap" }}>
          {translated("Page")} {pageNumber} / {totalPages}
        </p>
        <PDFFootnoteTextArea
          footnote={rightFootnote}
          setFootnote={setRightFootnote}
          textAlign="right"
        />
      </FlexRow>
    </footer>
  )
}

const PDFPage: FC<{
  site: Site | null
  user: ICloudUser | null
  pageNumber: number
  totalPages: number
  children: any
}> = ({ site, user, pageNumber, totalPages, children }) => {
  return (
    <section
      className="sheet p-8mm"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
      }}
    >
      <PDFHeader site={site} user={user} />
      {children}
      <PDFFooter pageNumber={pageNumber} totalPages={totalPages} />
    </section>
  )
}

// PAGE CONTENTS

const FirstPageContent: FC<{
  user: ICloudUser | null
  site: Site
  globalResult: EResult | null
  enterpriseCustomers: ICloudCustomer[] | null
}> = ({ user, site, globalResult, enterpriseCustomers }) => {
  const { translated } = useTranslationContext()

  const {
    leftSignatureDataURL,
    rightSignatureDataURL,
    leftSignatureName,

    setLeftSignatureDataURL,
    setRightSignatureDataURL,
    setLeftSignatureName,
  } = useReportConfigContext()

  const [clientAddressValue, setClientAddressValue] = useState<string | null>(null)
  const [clientContactValue, setClientContactValue] = useState<string | null>(null)

  useEffect(() => {
    let customer = enterpriseCustomers?.find((customer) => customer.Name === site.client_name)
    if (customer !== undefined) {
      setClientAddressValue(customer.PrimaryAddress ?? "")
      setClientContactValue(customer.Contact ?? "")
    }
  }, [enterpriseCustomers])

  const memoSitePicture = useMemo(() => {
    if (!site.pictures) {
      return <></>
    }
    let pictureUUID = site.pictures[0]
    if (!pictureUUID) {
      return <></>
    }
    return (
      <img
        key={pbUUIDToUuid(pictureUUID)}
        className="max-h-80mm object-contain border-2 border-primary-dark"
        style={{
          maxWidth: "80%",
        }}
        src={`/api/uploads/${pbUUIDToUuid(pictureUUID)}`}
        alt="site"
      />
    )
  }, [site])

  const memoUserFullName = useMemo(() => {
    if (!user) {
      return <PDFInput />
    }
    return <PDFInput key={Math.random()} defaultValue={user.FirstName + " " + user.LastName} />
  }, [user])

  const memoUserPhoneNumber = useMemo(() => {
    if (!user) {
      return <PDFInput />
    }
    let value = user.PhoneNumber
    if (user.Enterprise !== null && user.Enterprise.EnterprisePhoneNumber) {
      value = user.Enterprise.EnterprisePhoneNumber
    }
    return <PDFInput key={Math.random()} defaultValue={value} />
  }, [user])

  const memoUserEmail = useMemo(() => {
    if (!user) {
      return <PDFInput />
    }
    let value = user.Email
    if (user.Enterprise !== null && user.Enterprise.EnterpriseEmail) {
      value = user.Enterprise.EnterpriseEmail
    }
    return <PDFInput key={Math.random()} defaultValue={value} />
  }, [user])

  const memoUserEnterpriseCofracPictureUrl = useMemo((): string | null => {
    if (!user || !user.Enterprise || !user.Enterprise.CofracPictureUUID) {
      return null
    }
    return `/api/uploads/${user.Enterprise.CofracPictureUUID}`
  }, [user])

  const memoUserEnterpriseCofracLegalNotice = useMemo((): string | null => {
    if (!user || !user.Enterprise) {
      return null
    }
    return user.Enterprise.CofracLegalNotice
  }, [user])

  const memoSignaturesRow = useMemo(() => {
    let userPDFInput = (
      <PDFInput
        key={"nouser"}
        defaultValue={"   "}
        style={{
          width: "100%",
          textAlign: "center",
        }}
      />
    )
    if (user !== null) {
      userPDFInput = (
        <PDFInput
          key={user.UUID}
          defaultValue={user?.FirstName + " " + user?.LastName}
          style={{
            width: "fit-content",
            textAlign: "center",
          }}
        />
      )
    }

    return (
      <Row justify="space-between">
        <Col xs={12}>
          <FlexCol
            style={{
              height: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <PDFHidableSpan>
              <span style={{ width: "100%", textAlign: "center" }}>
                <Translated keyEn="Head of inspection" />
              </span>
            </PDFHidableSpan>
            <FlexCol style={{ alignItems: "center" }}>
              <ImageUploader
                dataURL={leftSignatureDataURL}
                setDataURL={setLeftSignatureDataURL}
                size={50}
                icon={
                  <SignatureOutlined
                    className="pdf-invisible"
                    style={{ fontSize: "2rem", color: COLOR_BG_GRAY }}
                  />
                }
              />
              <PDFControlledInput
                value={leftSignatureName ?? ""}
                setValue={(v) => setLeftSignatureName(v?.toString() ?? "")}
                inputProps={{
                  style: {
                    width: "fit-content",
                    textAlign: "center",
                  },
                }}
              />
            </FlexCol>
          </FlexCol>
        </Col>
        <Col xs={12}>
          <FlexCol
            style={{
              height: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <PDFHidableSpan>
              <Translated keyEn="Inspector" />
            </PDFHidableSpan>
            <FlexCol style={{ alignItems: "center" }}>
              <ImageUploader
                dataURL={rightSignatureDataURL}
                setDataURL={setRightSignatureDataURL}
                size={50}
                icon={
                  <SignatureOutlined
                    className="pdf-invisible"
                    style={{ fontSize: "2rem", color: COLOR_BG_GRAY }}
                  />
                }
              />
              {userPDFInput}
            </FlexCol>
          </FlexCol>
        </Col>
      </Row>
    )
  }, [
    user,
    leftSignatureDataURL,
    rightSignatureDataURL,
    leftSignatureName,
    setLeftSignatureDataURL,
    setRightSignatureDataURL,
    setLeftSignatureName,
  ])

  const memoCofracRow = useMemo(() => {
    if (
      memoUserEnterpriseCofracPictureUrl === null ||
      memoUserEnterpriseCofracLegalNotice === null
    ) {
      return null
    }
    return (
      <FlexRow
        style={{
          justifyContent: "center",
          alignItems: "end",
          gap: 0,
        }}
      >
        <ImageUploader
          dataURL={memoUserEnterpriseCofracPictureUrl}
          setDataURL={() => null}
          size={50}
        />
        <PDFInputTextArea
          defaultValue={memoUserEnterpriseCofracLegalNotice}
          autoSize
          style={{
            fontSize: "0.7rem",
            marginLeft: 0,
          }}
        />
      </FlexRow>
    )
  }, [memoUserEnterpriseCofracLegalNotice, memoUserEnterpriseCofracPictureUrl])

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  const memoClientContactInput = useMemo(() => {
    let text = ""
    if (clientContactValue !== null && clientContactValue !== "") {
      text += clientContactValue
    } else {
      text += "—"
    }
    if (clientAddressValue !== null && clientAddressValue !== "") {
      text += "\n" + clientAddressValue
    }
    return (
      <PDFInputTextArea
        key={Math.random()}
        defaultValue={text}
        autoSize={{ minRows: 2 }}
        style={{
          width: "fit-content",
          minWidth: "50%",
        }}
      />
    )
  }, [clientContactValue, clientAddressValue])

  const memoGlobalResultTag = useMemo(() => {
    if (globalResult === null) {
      return <PDFInput />
    }
    switch (site.norm_type) {
      case NormType.EN_1177:
      case NormType.EN_12503:
      case NormType.EN_14960:
        return <EResultTagAdequacy result={globalResult} />
      case NormType.EN_ISO_23659:
        return <EResultTagTL result={globalResult} />
      default:
        return <PDFInput />
    }
  }, [globalResult, site.norm_type])

  return (
    <FlexCol
      style={{
        justifyContent: "space-between",
        alignItems: "start",
        height: `100%`,
        paddingBottom: FOOTER_HEIGHT,
      }}
    >
      {/* Client coordinates */}
      <FlexCol
        style={{
          alignItems: "start",
          gap: 0,
          width: "100%",
        }}
      >
        <span style={{ fontSize: "1rem", fontWeight: "bold", marginTop: 25, marginLeft: 2 }}>
          <Translated keyEn="Delivered to" />:
        </span>
        <b>
          <PDFInput defaultValue={site.client_name} />
        </b>
        {memoClientContactInput}
      </FlexCol>
      {/* Names: mission, client, site */}
      <FlexCol
        style={{
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0,
        }}
      >
        {/* Mission */}
        <FlexRow
          style={{
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "1.7rem",
          }}
        >
          <span>
            <Translated keyEn="Mission" />:
          </span>
          <span>
            <PDFInput
              defaultValue={site.mission_name}
              style={{
                width: "fit-content",
              }}
            />
          </span>
        </FlexRow>
        {/* Site */}
        <FlexRow
          style={{
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "1.3rem",
            borderBottom: `2px solid black`,
          }}
        >
          <span>
            <Translated keyEn="Site" />:
          </span>
          <span>
            <PDFInput
              defaultValue={site.site_name}
              style={{
                width: "fit-content",
              }}
            />
          </span>
        </FlexRow>
        <Divider />
        <Row gutter={[10, 10]} justify="center" style={{ width: "100%" }}>
          <Col xs={14}>
            {/* Details and contact */}
            <FlexCol
              style={{
                gap: 0,
              }}
            >
              <FlexCol>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    fontWeight: "bold",
                  }}
                >
                  <Translated keyEn="Site address" />:
                </span>
                <PDFInputTextArea defaultValue={site.address} autoSize />
              </FlexCol>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    whiteSpace: "nowrap",
                    fontWeight: "bold",
                  }}
                >
                  <Translated keyEn="Inspection date" />:
                </span>
                <PDFInput defaultValue={new Date(site.execution_date).toLocaleDateString()} />
              </FlexRow>
              <span style={{ fontWeight: "bold", marginTop: 25, marginBottom: 5 }}>
                <Translated keyEn="Inspected by" />:
              </span>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    whiteSpace: "nowrap",
                    fontWeight: "bold",
                  }}
                >
                  <Translated keyEn="Inspector" />:
                </span>
                {memoUserFullName}
              </FlexRow>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    whiteSpace: "nowrap",
                    fontWeight: "bold",
                  }}
                >
                  <Translated keyEn="Email" />:{" "}
                </span>
                {memoUserEmail}
              </FlexRow>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    whiteSpace: "nowrap",
                    fontWeight: "bold",
                  }}
                >
                  <Translated keyEn="Phone" />:{" "}
                </span>
                {memoUserPhoneNumber}
              </FlexRow>
            </FlexCol>
          </Col>
          <Col xs={10}>
            {/* Photo */}
            {memoSitePicture}
          </Col>
        </Row>
      </FlexCol>
      <FlexRow
        style={{
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <PDFHidableSpan>
          <span
            style={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
          >
            <Translated keyEn="Global result" />: {memoGlobalResultTag}
          </span>
        </PDFHidableSpan>
      </FlexRow>
      <Row justify="space-between" align="bottom" style={{ width: "100%" }}>
        <Col xs={12}>{memoCofracRow}</Col>
        <Col xs={12}>{memoSignaturesRow}</Col>
      </Row>
    </FlexCol>
  )
}

const ReportNumberedTitle: FC<{ title: ReactElement; number: number }> = ({ title, number }) => {
  return (
    <Typography.Text style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
      {number}. {title}
    </Typography.Text>
  )
}

const GeolocationPartContent: FC<{
  site: Site
  zoneImpactsMap: Record<string, Impact[]> | null
}> = ({ site, zoneImpactsMap }) => {
  const { translated } = useTranslationContext()

  const [coordinates, setCoordinates] = useState<ICoordinate | null>(null)

  const memoImpactMarkerCoordinates = useMemo((): LabeledTsCoordinate[] | null => {
    let _markers: LabeledTsCoordinate[] = []
    if (zoneImpactsMap === null) {
      return _markers
    }
    for (let [_, impacts] of Object.entries(zoneImpactsMap)) {
      for (let impact of impacts) {
        if (impact.latitude === 0 || impact.longitude === 0) {
          continue
        }
        let impactUUID = pbUUIDToUuid(impact.uuid)
        _markers.push({
          key: impactUUID,
          timestamp: impact.created_at,
          lat: impact.latitude,
          lng: impact.longitude,
          header: (
            <Link to={`/impacts/${impactUUID}`}>
              <b>Impact</b> <FontAwesomeIcon icon={faExternalLink} />
            </Link>
          ),
          description: (
            <FlexCol>
              <div>
                <b>Gmax:</b> {impact.impact_gmax.toFixed(0)}g
              </div>
              <div>
                <b>HIC:</b> {impact.impact_hic.toFixed(0)}
              </div>
            </FlexCol>
          ),
        })
      }
    }
    // console.log(`SitePage: memoImpactMarkerCoordinates`, _markers)
    return _markers
  }, [zoneImpactsMap])

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  const memoGeolocationInputText = useMemo(() => {
    let text = memoMustBeTranslated("The site is located at the following coordinates") + ": "
    // console.log(`SitePage: memoGeolocationInputText`, coordinates)
    if (coordinates !== null) {
      let latitudeLetter = "N"
      if (coordinates.coords.latitude < 0) {
        latitudeLetter = "S"
      }
      let longitudeLetter = "E"
      if (coordinates.coords.longitude < 0) {
        longitudeLetter = "W"
      }
      text += "\n"
      text += `• ${latitudeLetter} ${decimalCoordinateToDegreesMinutesSeconds(coordinates.coords.latitude)} (${coordinates.coords.latitude.toFixed(6)})\n`
      text += `• ${longitudeLetter} ${decimalCoordinateToDegreesMinutesSeconds(coordinates.coords.longitude)} (${coordinates.coords.longitude.toFixed(6)})`
    }
    return <PDFInputTextArea key={Math.random()} defaultValue={text} autoSize={{ minRows: 3 }} />
  }, [memoMustBeTranslated, coordinates])

  return (
    <>
      <div style={PDF_INPUT_TEXT_AREA_STYLE}>{memoGeolocationInputText}</div>
      <GoogleAddressWidget
        address={site.address}
        setAddress={() => null}
        points={memoImpactMarkerCoordinates}
        autocompleteClassName="pdf-invisible"
        externalSetSelectedCoordinates={setCoordinates}
      />
    </>
  )
}

const ReferenceEquipmentPartContent: FC<{
  device: ICloudDevice | null
  deviceEvents: ICloudDeviceEvent[] | null
}> = ({ device, deviceEvents }) => {
  const { translated } = useTranslationContext()

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  if (device === null) {
    return <PDFInputTextArea defaultValue={"  "} autoSize />
  }

  let text = memoMustBeTranslated("Tests were performed using the equipment: ")
  text += `\"${device.DeviceType?.Name}\" S/N: ${device.Name} (#${device.SerialNumber})` + "."
  if (device.LastMetrology !== null && device.NextMetrology !== null) {
    text +=
      " " +
      memoMustBeTranslated("Metrology valid from") +
      " " +
      dayjs(device.LastMetrology).format("DD-MM-YYYY") +
      " " +
      memoMustBeTranslated("to") +
      " " +
      dayjs(device.NextMetrology).format("DD-MM-YYYY") +
      "."
  }
  if (deviceEvents !== null) {
    let lastMetrologyEvent: ICloudDeviceEvent | null = null
    for (let event of deviceEvents) {
      if (event.EventType === ECloudDeviceEventType.Metrology) {
        lastMetrologyEvent = event
        break
      }
    }
    if (lastMetrologyEvent !== null) {
      text +=
        " " +
        memoMustBeTranslated("Metrology reference") +
        ": " +
        "EVE" +
        lastMetrologyEvent.ID.toString().padStart(4, "0") +
        " " +
        memoMustBeTranslated("of") +
        " " +
        dayjs(lastMetrologyEvent.StartDate).format("DD-MM-YYYY") +
        "."
      for (let file of lastMetrologyEvent.Files || []) {
        text += `\n\t• ${file.OriginalName}`
      }
    }
  }
  // console.log(`SecondPageContent: memoReferenceEquipmentText`, text)
  return <PDFInputTextArea key={Math.random()} defaultValue={text} autoSize />
}

const MethodologyPartContent: FC<{
  user: ICloudUser | null
  site: Site
}> = ({ user, site }) => {
  const { translated } = useTranslationContext()

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  const normTypeStringCallback = useCallback(
    (normType: NormType): string => {
      let normTypeStr = normTypeString(normType)
      const swissEnterprises = ["EXPAIRES", "TOPJEUX"]
      if (
        user !== null &&
        user.Enterprise !== null &&
        swissEnterprises.includes(user.Enterprise.Name.toUpperCase())
      ) {
        normTypeStr = "SN/" + normTypeStr
      }
      return normTypeStr
    },
    [user],
  )

  const memoNormType = useMemo((): string => {
    return normTypeStringCallback(site.norm_type)
  }, [site, normTypeStringCallback])

  const memoMethodologyInput = useMemo((): ReactElement => {
    let text = memoMustBeTranslated(
      "The tests were carried out in accordance with the following procedure.",
    )
    text += "\n"
    switch (site.norm_type) {
      case NormType.EN_1177:
        text += `${memoNormType}: ${memoMustBeTranslated("Impact absorbing playground floors: impact attenuation test method.")} `
        switch (site.method_type_en_1177) {
          case MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION:
            text += memoMustBeTranslated(
              `Determination of the critical fall height of an equipment consists of measuring values of Gmax and HIC by falling an impactor at different fall heights. A dependency curve of HIC/Gmax over fall height is found. Critical fall height (CFH) is defined as the height at which either Gmax or HIC curve meet their corresponding limits: 200g or 1000 HIC.`,
            )
            break
          case MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE:
            text += memoMustBeTranslated(
              `Impact attenuation compliance consists of measuring values of Gmax and HIC by falling an impactor at a fixed fall height. The values are compared to those declared by the manufacturer.`,
            )
            break
        }
        break
      case NormType.EN_12503:
        text += `${memoNormType}: ${memoMustBeTranslated("Sports mats: determination of shock absorption.")} `
        text += memoMustBeTranslated(
          `The test consists of dropping an impactor from a fixed height on the sports mat. Impact shock, retained energy and flexion are measured on different locations on the sports mat. Values are compared to those defined in the norm, according to sports mat type.`,
        )
        break
      case NormType.EN_14960:
        text += `${memoNormType}: ${memoMustBeTranslated("Inflatable play equipment: safety requirements and test methods.")} `
        text += memoMustBeTranslated(
          `The test consists of dropping an impactor from a fixed height on an airbag or inflatable structure. The impactor is equipped with an accelerometer to measure the deceleration. The test is repeated at different locations on the inflatable structure.`,
        )
        break
      case NormType.EN_ISO_23659:
        text += `${memoNormType}: ${memoMustBeTranslated("Recreational and sports installations: trampoline parks - safety requirements and test methods.")} `
        text += memoMustBeTranslated(
          `The test consists of dropping an impactor from a fixed height on a pre-installed trampoline. Impact shock, re-bounce height are measured and trampoline's "Performance Factor" is calculated using the approach defined in the norm.`,
        )
        break
    }
    return <PDFInputTextArea key={Math.random()} defaultValue={text} autoSize />
  }, [memoMustBeTranslated, site, memoNormType])

  return <div style={PDF_INPUT_TEXT_AREA_STYLE}>{memoMethodologyInput}</div>
}

const EnvironmentalConditionsPartContent: FC<{
  site: Site
  siteEquipmentsMap: Record<string, Equipment[]> | null
  equipmentZonesMap: Record<string, Zone[]> | null
  zoneImpactsMap: Record<string, Impact[]> | null
}> = ({ site, siteEquipmentsMap, equipmentZonesMap, zoneImpactsMap }) => {
  const { translated } = useTranslationContext()

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  const memoEquipments = useMemo(() => {
    let _equipments: Equipment[] = []
    for (let siteUUID in siteEquipmentsMap) {
      let equipments = siteEquipmentsMap[siteUUID]
      if (equipments === undefined) {
        continue
      }
      _equipments.push(...equipments)
    }
    return _equipments
  }, [siteEquipmentsMap])

  const memoEnvironmentalConditionsText = useMemo((): string => {
    let text = ""
    text += memoMustBeTranslated(
      "This test was carried out on site with the particular climatic and site conditions occurring on the day of test. The results cannot be used to indicate the performance of the product under any other conditions or in other locations.",
    )
    return text
  }, [memoMustBeTranslated, site])

  return (
    <>
      {memoEnvironmentalConditionsText && (
        <div style={PDF_INPUT_TEXT_AREA_STYLE}>
          <PDFInputTextArea
            defaultValue={memoEnvironmentalConditionsText}
            autoSize={{ minRows: 3 }}
          />
        </div>
      )}
      <SiteEnvironmentalConditionsTable_PF
        siteUUID={site.uuid}
        equipments={memoEquipments}
        equipmentZonesMap={equipmentZonesMap}
        zoneImpactsMap={zoneImpactsMap}
      />
    </>
  )
}

const SecondPageContent: FC<{
  user: ICloudUser | null
  device: ICloudDevice | null
  deviceEvents: ICloudDeviceEvent[] | null
  site: Site
  siteEquipmentsMap: Record<string, Equipment[]> | null
  equipmentZonesMap: Record<string, Zone[]> | null
  zoneImpactsMap: Record<string, Impact[]> | null
  primaryPageMode: PDFPrimaryPagesMode
}> = ({
  user,
  device,
  deviceEvents,
  site,
  siteEquipmentsMap,
  equipmentZonesMap,
  zoneImpactsMap,
  primaryPageMode,
}) => {
  const { translated } = useTranslationContext()
  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  const memoEquipments = useMemo(() => {
    let _equipments: Equipment[] = []
    for (let siteUUID in siteEquipmentsMap) {
      let equipments = siteEquipmentsMap[siteUUID]
      if (equipments === undefined) {
        continue
      }
      _equipments.push(...equipments)
    }
    return _equipments
  }, [siteEquipmentsMap])

  const normTypeStringCallback = useCallback(
    (normType: NormType): string => {
      let normTypeStr = normTypeString(normType)
      const swissEnterprises = ["EXPAIRES", "TOPJEUX"]
      if (
        user !== null &&
        user.Enterprise !== null &&
        swissEnterprises.includes(user.Enterprise.Name.toUpperCase())
      ) {
        normTypeStr = "SN/" + normTypeStr
      }
      return normTypeStr
    },
    [user],
  )

  const memoNormType = useMemo((): string => {
    return normTypeStringCallback(site.norm_type)
  }, [site, normTypeStringCallback])

  const memoMethodTypeEN1177 = useMemo((): string => {
    switch (site.method_type_en_1177) {
      case MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION:
        return "Method 1: Critical Fall Height"
      case MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE:
        return "Method 2: Adequacy"
      default:
        return ""
    }
  }, [site])

  const memoIntroductionInput = useMemo((): ReactElement => {
    // norm
    let text =
      memoMustBeTranslated(
        "The following report presents the results of the tests carried out on the site",
      ) +
      ` "${site.site_name}" ` +
      memoMustBeTranslated("in accordance with the norm") +
      " " +
      memoNormType

    //method if applicable
    if (memoMethodTypeEN1177 !== "") {
      text +=
        " " +
        memoMustBeTranslated("using the") +
        " " +
        `${memoMustBeTranslated(memoMethodTypeEN1177)}`
    }

    // date
    text += " " + memoMustBeTranslated("on") + " " + formatDayOrLongDash(site.execution_date) + "."

    // EN 1176 Disclaimer
    if (site.norm_type === NormType.EN_1177) {
      text += "\n"
      text +=
        memoMustBeTranslated("NB: Control points of the") +
        " " +
        normTypeStringCallback(NormType.EN_1176) +
        " " +
        memoMustBeTranslated(
          "standard are not taken into account in this report. Please refer to the last annual general inspection",
        ) +
        " " +
        `(${normTypeStringCallback(NormType.EN_1176)}-7).`
    }

    // reference procedure
    // text += "\n"
    // text += memoMustBeTranslated("Reference procedure: N/A")

    return (
      <PDFInputTextArea
        key={Math.random()}
        defaultValue={text}
        autoSize={{
          minRows: 3,
        }}
      />
    )
  }, [memoMustBeTranslated, site, memoNormType, memoMethodTypeEN1177, normTypeStringCallback])

  const memoEquipmentMetadataTable = useMemo(() => {
    switch (site.norm_type) {
      case NormType.EN_1177:
        return (
          <SiteEquipmentsTableMetadataPF_Print
            siteUUID={site.uuid}
            equipments={memoEquipments}
            equipmentZonesMap={equipmentZonesMap}
            zoneImpactsMap={zoneImpactsMap}
            methodType={site.method_type_en_1177}
          />
        )
      case NormType.EN_12503:
        return (
          <SiteEquipmentsTableMetadataSM_Print
            siteUUID={site.uuid}
            equipments={memoEquipments}
            equipmentZonesMap={equipmentZonesMap}
            zoneImpactsMap={zoneImpactsMap}
          />
        )
      case NormType.EN_ISO_23659:
        return (
          <SiteEquipmentsTableMetadataTL_Print
            siteUUID={site.uuid}
            equipments={memoEquipments}
            equipmentZonesMap={equipmentZonesMap}
            zoneImpactsMap={zoneImpactsMap}
          />
        )
      default:
        return null
    }
  }, [site, memoEquipments, equipmentZonesMap, zoneImpactsMap])

  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      {/* Introduction */}
      <ReportNumberedTitle number={1} title={<Translated keyEn="Introduction" />} />
      <div style={PDF_INPUT_TEXT_AREA_STYLE}>{memoIntroductionInput}</div>
      {/* Identification */}
      <ReportNumberedTitle number={2} title={<Translated keyEn="Identification" />} />
      <div style={PDF_INPUT_TEXT_AREA_STYLE}>
        <PDFInputTextArea
          defaultValue={memoMustBeTranslated("Tests were carried out on the following equipment")}
          autoSize
        />
      </div>
      {memoEquipmentMetadataTable}
      {/* Methodology */}
      {(primaryPageMode === PDFPrimaryPagesMode.ExpandedMode ||
        primaryPageMode === PDFPrimaryPagesMode.CompactMode) && (
        <>
          <ReportNumberedTitle number={3} title={<Translated keyEn="Methodology" />} />
          <MethodologyPartContent user={user} site={site} />
        </>
      )}
      {/* Reference equipment */}
      {primaryPageMode === PDFPrimaryPagesMode.CompactMode && (
        <>
          <ReportNumberedTitle number={4} title={<Translated keyEn="Reference equipment" />} />
          <div style={PDF_INPUT_TEXT_AREA_STYLE}>
            <ReferenceEquipmentPartContent device={device} deviceEvents={deviceEvents} />
          </div>
        </>
      )}
    </FlexCol>
  )
}

const ThirdPageContent: FC<{
  user: ICloudUser | null
  device: ICloudDevice | null
  deviceEvents: ICloudDeviceEvent[] | null
  site: Site
  siteEquipmentsMap: Record<string, Equipment[]> | null
  equipmentZonesMap: Record<string, Zone[]> | null
  zoneImpactsMap: Record<string, Impact[]> | null
  primaryPageMode: PDFPrimaryPagesMode
}> = ({
  user,
  device,
  deviceEvents,
  site,
  siteEquipmentsMap,
  equipmentZonesMap,
  zoneImpactsMap,
  primaryPageMode,
}) => {
  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      {/* Methodology */}
      {primaryPageMode === PDFPrimaryPagesMode.SuperExpandedMode && (
        <>
          <ReportNumberedTitle number={3} title={<Translated keyEn="Methodology" />} />
          <MethodologyPartContent user={user} site={site} />
        </>
      )}
      {/* Reference equipment */}
      {(primaryPageMode === PDFPrimaryPagesMode.ExpandedMode ||
        primaryPageMode === PDFPrimaryPagesMode.SuperExpandedMode) && (
        <>
          <ReportNumberedTitle number={4} title={<Translated keyEn="Reference equipment" />} />
          <div style={PDF_INPUT_TEXT_AREA_STYLE}>
            <ReferenceEquipmentPartContent device={device} deviceEvents={deviceEvents} />
          </div>
        </>
      )}
      {/* Environmental conditions */}
      {(primaryPageMode === PDFPrimaryPagesMode.CompactMode ||
        primaryPageMode === PDFPrimaryPagesMode.ExpandedMode) && (
        <>
          <ReportNumberedTitle number={5} title={<Translated keyEn="Environmental conditions" />} />
          <EnvironmentalConditionsPartContent
            site={site}
            siteEquipmentsMap={siteEquipmentsMap}
            equipmentZonesMap={equipmentZonesMap}
            zoneImpactsMap={zoneImpactsMap}
          />
        </>
      )}
      {primaryPageMode === PDFPrimaryPagesMode.SuperExpandedMode && (
        <>
          <ReportNumberedTitle number={5} title={<Translated keyEn="Geolocation" />} />
          <GeolocationPartContent site={site} zoneImpactsMap={zoneImpactsMap} />
        </>
      )}
      {primaryPageMode === PDFPrimaryPagesMode.CompactMode && (
        <>
          <ReportNumberedTitle number={6} title={<Translated keyEn="Geolocation" />} />
          <GeolocationPartContent site={site} zoneImpactsMap={zoneImpactsMap} />
          {/* Comments */}
          <div style={PDF_INPUT_TEXT_AREA_STYLE}>
            <PDFInputTextArea defaultValue={""} autoSize={{ minRows: 2 }} />
          </div>
        </>
      )}
    </FlexCol>
  )
}

const FourthPageContent: FC<{
  site: Site
  siteEquipmentsMap: Record<string, Equipment[]> | null
  equipmentZonesMap: Record<string, Zone[]> | null
  zoneImpactsMap: Record<string, Impact[]> | null
  primaryPageMode: PDFPrimaryPagesMode
}> = ({ site, siteEquipmentsMap, equipmentZonesMap, zoneImpactsMap, primaryPageMode }) => {
  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      {primaryPageMode === PDFPrimaryPagesMode.ExpandedMode && (
        <>
          <ReportNumberedTitle number={6} title={<Translated keyEn="Geolocation" />} />
          <GeolocationPartContent site={site} zoneImpactsMap={zoneImpactsMap} />
        </>
      )}
      {primaryPageMode === PDFPrimaryPagesMode.SuperExpandedMode && (
        <>
          <ReportNumberedTitle number={6} title={<Translated keyEn="Environmental conditions" />} />
          <EnvironmentalConditionsPartContent
            site={site}
            siteEquipmentsMap={siteEquipmentsMap}
            equipmentZonesMap={equipmentZonesMap}
            zoneImpactsMap={zoneImpactsMap}
          />
        </>
      )}
      {/* Comments */}
      <div style={PDF_INPUT_TEXT_AREA_STYLE}>
        <PDFInputTextArea defaultValue={""} autoSize={{ minRows: 2 }} />
      </div>
    </FlexCol>
  )
}

const AnnexPageContent: FC<{
  user: ICloudUser | null
  site: Site
  annexes: Annex[]
}> = ({ annexes }) => {
  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      {annexes.map((annex, i) => {
        return (
          <>
            <div key={pbUUIDToUuid(annex.uuid)}>
              <h2>
                <Translated keyEn="Annex" /> {annex.annex_idx}
              </h2>
              <Row gutter={[10, 10]} justify="space-between">
                <Col xs={14}>
                  <div style={PDF_INPUT_TEXT_AREA_STYLE}>
                    <PDFInputTextArea
                      defaultValue={annex.annex_content}
                      autoSize={{ minRows: 3 }}
                    />
                  </div>
                </Col>
                <Col xs={10}>
                  <FirstPicture pictures={annex.pictures} />
                </Col>
              </Row>
            </div>
            {i === annexes.length - 1 ? <PDFEmptySpacePlaceholder /> : <Divider />}
          </>
        )
      })}
    </FlexCol>
  )
}

interface IEquipmentPageContentProps {
  user: ICloudUser | null
  site: Site
  equipment: Equipment
  equipmentZonesMap: Record<string, Zone[]> | null
  zoneImpactsMap: Record<string, Impact[]> | null
  // Page mode related props
  equipmentPageMode: PDFEquipmentPageMode
  multiPageIndex: number
}

const EquipmentPageContentSM: FC<IEquipmentPageContentProps> = ({
  user,
  site,
  equipment,
  equipmentZonesMap,
  zoneImpactsMap,
  equipmentPageMode: equipmnetPageMode,
  multiPageIndex,
}) => {
  const mooSyncRef = useRef<uPlot.SyncPubSub | null>(null)

  const memoSportsMatTypeDescription = useMemo(() => {
    if (equipment.sports_mat_type === null) {
      return ""
    }
    return `Type "${equipment.sports_mat_type}": ${sportsMatTypeDescriptionStr(equipment.sports_mat_type)}`
  }, [equipment])

  const memoSportsMatThicknessMeanAndStd = useMemo((): MeanWithStd | null => {
    return getEquipmentSportsMatThicknessMeanAndStd([
      equipment.sports_mat_thickness_side_one,
      equipment.sports_mat_thickness_side_two,
      equipment.sports_mat_thickness_side_three,
      equipment.sports_mat_thickness_side_four,
    ])
  }, [equipment])

  const memoEquipmentZones = useMemo(() => {
    if (equipmentZonesMap === null) {
      return []
    }
    return equipmentZonesMap[pbUUIDToUuid(equipment.uuid)] ?? []
  }, [equipmentZonesMap])

  const memoRenderedSportsMatThicknessMeanAndStd = useMemo(() => {
    if (memoSportsMatThicknessMeanAndStd === null) {
      return ""
    }
    return `${memoSportsMatThicknessMeanAndStd.mean.toFixed(0)} ± ${memoSportsMatThicknessMeanAndStd.std.toFixed(1)}`
  }, [memoSportsMatThicknessMeanAndStd])

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Equipment" />:
          </span>
          <PDFInput defaultValue={equipment.equipment_name} />
        </FlexRow>
      </h2>
      <FlexRow
        style={{
          alignItems: "center",
          width: "100%",
        }}
      >
        <span>
          <Translated keyEn="Type" />:
        </span>
        <PDFInput defaultValue={memoSportsMatTypeDescription} />
      </FlexRow>
      <RequirementsAlertSM smType={equipment.sports_mat_type} />
      <Row gutter={[10, 10]} style={{ width: "100%" }}>
        <Col xs={12}>
          <h3>
            <Translated keyEn="References" />
          </h3>
          <FlexCol style={{ gap: 0 }}>
            {/* Manufacturer */}
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Manufacturer" />:
              </span>
              <PDFInput
                defaultValue={equipment.equipment_manufacturer}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
            {/* Reference (Model) */}
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Reference" />:
              </span>
              <PDFInput
                defaultValue={equipment.equipment_reference}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
            {/* Installation date */}
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Installation&nbsp;date" />:
              </span>
              <PDFInput
                defaultValue={formatDayOrLongDash(equipment.equipment_installation_date, true)}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
          </FlexCol>
        </Col>
        <Col xs={12}>
          <h3>
            <Translated keyEn="Measurements" />
          </h3>
          <FlexCol style={{ gap: 0 }}>
            <FlexRow
              style={{
                alignItems: "center",
                width: "100%",
              }}
            >
              <span>
                <Translated keyEn="Length,&nbsp;cm" />:
              </span>
              <PDFInput
                defaultValue={Math.round(equipment.sports_mat_length / 10)}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
            <FlexRow
              style={{
                alignItems: "center",
                width: "100%",
              }}
            >
              <span>
                <Translated keyEn="Width,&nbsp;cm" />:
              </span>
              <PDFInput
                defaultValue={Math.round(equipment.sports_mat_width / 10)}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Thickness,&nbsp;mm" />:
              </span>
              <PDFInput
                defaultValue={memoRenderedSportsMatThicknessMeanAndStd}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
          </FlexCol>
          <Col xs={10}>
            <FirstPicture pictures={equipment.pictures} />
          </Col>
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per zone" />
      </h3>
      <EquipmentZonesTableSM_Print
        equipmentUUID={equipment.uuid}
        smThickness={memoSportsMatThicknessMeanAndStd}
        smType={equipment.sports_mat_type}
        zones={memoEquipmentZones}
      />
    </FlexCol>
  )
}

const EquipmentPageContentPF_CFH: FC<IEquipmentPageContentProps> = ({
  user,
  site,
  equipment,
  equipmentZonesMap,
  zoneImpactsMap,
  equipmentPageMode,
  multiPageIndex,
}) => {
  const memoEquipmentZones = useMemo(() => {
    if (equipmentZonesMap === null) {
      return []
    }
    return equipmentZonesMap[pbUUIDToUuid(equipment.uuid)] ?? []
  }, [equipmentZonesMap])

  if (equipmentPageMode === PDFEquipmentPageMode.MultiPage && multiPageIndex > 0) {
    return (
      <FlexCol
        style={{
          justifyContent: "start",
          alignItems: "start",
          maxHeight: "90%",
          overflow: "hidden",
        }}
      >
        <h2
          style={{
            width: "100%",
          }}
        >
          <FlexRow
            style={{
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>
              <Translated keyEn="Equipment" />:
            </span>
            <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
          </FlexRow>
        </h2>
        <h3>
          <Translated keyEn="Results per zone" /> <Translated keyEn="(continued)" />
        </h3>
        <EquipmentZonesTablePF_CFH_Print
          equipmentUUID={equipment.uuid}
          zones={memoEquipmentZones}
        />
        {equipmentPageMode === PDFEquipmentPageMode.MultiPage && multiPageIndex === 0 && (
          <Typography.Text italic>
            <Translated keyEn="Table continued on next page..." />
          </Typography.Text>
        )}
      </FlexCol>
    )
  }

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Equipment" />:
          </span>
          <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} style={{ width: "100%" }}>
        <Col xs={14}>
          <FlexCol>
            <div>
              <h3>
                <Translated keyEn="Equipment references" />
              </h3>
              <FlexCol style={{ gap: 0 }}>
                {/* Manufacturer */}
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Manufacturer" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.equipment_manufacturer}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                {/* Reference (Model) */}
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Reference" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.equipment_reference}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                {/* Installation date */}
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Installation&nbsp;date" />:
                  </span>
                  <PDFInput
                    defaultValue={formatDayOrLongDash(equipment.equipment_installation_date, true)}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
              </FlexCol>
            </div>
            <div>
              <h3>
                <Translated keyEn="Floor references" />
              </h3>
              <FlexCol style={{ gap: 0 }}>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Manufacturer" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_manufacturer}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Reference" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_reference}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Substrate" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_substrat}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Condition" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_condition}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Type" />:
                  </span>
                  <PDFInput defaultValue={equipment.floor_kind} style={{ width: "fit-content" }} />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Installation&nbsp;date" />:
                  </span>
                  <PDFInput
                    defaultValue={formatDayOrLongDash(equipment.floor_installation_date, true)}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
              </FlexCol>
            </div>
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={equipment.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per zone" />
      </h3>
      <EquipmentZonesTablePF_CFH_Print equipmentUUID={equipment.uuid} zones={memoEquipmentZones} />
      {equipmentPageMode === PDFEquipmentPageMode.MultiPage && (
        <Typography.Text italic>
          <Translated keyEn="Table continued on next page..." />
        </Typography.Text>
      )}
    </FlexCol>
  )
}

const EquipmentPageContentPF_ADQ: FC<IEquipmentPageContentProps> = ({
  user,
  site,
  equipment,
  equipmentZonesMap,
  zoneImpactsMap,
  equipmentPageMode,
  multiPageIndex,
}) => {
  const memoEquipmentZones = useMemo((): Zone[] => {
    if (equipmentZonesMap === null) {
      return []
    }
    return equipmentZonesMap[pbUUIDToUuid(equipment.uuid)] ?? []
  }, [equipmentZonesMap])

  if (equipmentPageMode === PDFEquipmentPageMode.MultiPage && multiPageIndex > 0) {
    return (
      <FlexCol
        style={{
          justifyContent: "start",
          alignItems: "start",
          maxHeight: "90%",
          overflow: "hidden",
        }}
      >
        <h2
          style={{
            width: "100%",
          }}
        >
          <FlexRow
            style={{
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>
              <Translated keyEn="Equipment" />:
            </span>
            <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
          </FlexRow>
        </h2>
        <h3>
          <Translated keyEn="Results per zone" /> <Translated keyEn="(continued)" />
        </h3>
        <EquipmentZonesTablePF_ADQ_Print
          equipmentUUID={equipment.uuid}
          zones={memoEquipmentZones}
        />
      </FlexCol>
    )
  }

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Equipment" />:
          </span>
          <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} justify="space-between" style={{ width: "100%" }}>
        <Col xs={14}>
          <FlexCol>
            <div>
              <h3>
                <Translated keyEn="Equipment references" />
              </h3>
              <FlexCol style={{ gap: 0 }}>
                {/* Manufacturer */}
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Manufacturer" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.equipment_manufacturer}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                {/* Reference (Model) */}
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Reference" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.equipment_reference}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                {/* Installation date */}
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Installation&nbsp;date" />:
                  </span>
                  <PDFInput
                    defaultValue={formatDayOrLongDash(equipment.equipment_installation_date, true)}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
              </FlexCol>
            </div>
            <div>
              <h3>
                <Translated keyEn="Floor references" />
              </h3>
              <FlexCol style={{ gap: 0 }}>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Manufacturer" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_manufacturer}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Reference" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_reference}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Substrate" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_substrat}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Condition" />:
                  </span>
                  <PDFInput
                    defaultValue={equipment.floor_condition}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Type" />:
                  </span>
                  <PDFInput defaultValue={equipment.floor_kind} style={{ width: "fit-content" }} />
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Installation&nbsp;date" />:
                  </span>
                  <PDFInput
                    defaultValue={formatDayOrLongDash(equipment.floor_installation_date, true)}
                    style={{ width: "fit-content" }}
                  />
                </FlexRow>
              </FlexCol>
            </div>
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={equipment.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per zone" />
      </h3>
      <EquipmentZonesTablePF_ADQ_Print equipmentUUID={equipment.uuid} zones={memoEquipmentZones} />
      {equipmentPageMode === PDFEquipmentPageMode.MultiPage && (
        <Typography.Text italic>
          <Translated keyEn="Table continued on next page..." />
        </Typography.Text>
      )}
    </FlexCol>
  )
}

const EquipmentPageContentTL: FC<IEquipmentPageContentProps> = ({
  user,
  site,
  equipment,
  equipmentZonesMap,
  zoneImpactsMap,
  equipmentPageMode,
  multiPageIndex,
}) => {
  const { translated } = useTranslationContext()
  const memoEquipmentZones = useMemo(() => {
    if (equipmentZonesMap === null) {
      return []
    }
    return equipmentZonesMap[pbUUIDToUuid(equipment.uuid)] ?? []
  }, [equipmentZonesMap])

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  if (equipmentPageMode === PDFEquipmentPageMode.MultiPage && multiPageIndex > 0) {
    return (
      <FlexCol
        style={{
          justifyContent: "start",
          alignItems: "start",
          maxHeight: "90%",
          overflow: "hidden",
        }}
      >
        <h2
          style={{
            width: "100%",
          }}
        >
          <FlexRow
            style={{
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>
              <Translated keyEn="Equipment" />:
            </span>
            <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
          </FlexRow>
        </h2>
        <h3>
          <Translated keyEn="Results per zone" /> <Translated keyEn="(continued)" />
        </h3>
        <EquipmentZonesTableTL_Print
          equipmentImpactorWeight={10}
          equipmentUUID={equipment.uuid}
          zones={memoEquipmentZones}
        />
      </FlexCol>
    )
  }

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Equipment" />:
          </span>
          <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} style={{ width: "100%" }}>
        <Col xs={12}>
          <h3>
            <Translated keyEn="References" />
          </h3>
          <FlexCol style={{ gap: 0 }}>
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Manufacturer" />:
              </span>
              <PDFInput defaultValue={equipment.equipment_manufacturer} />
            </FlexRow>
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Reference" />:
              </span>
              <PDFInput defaultValue={equipment.equipment_reference} />
            </FlexRow>
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Installation&nbsp;date" />:
              </span>
              <PDFInput
                defaultValue={formatDayOrLongDash(equipment.equipment_installation_date, true)}
              />
            </FlexRow>
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Length,&nbsp;cm" />:
              </span>
              <PDFInput defaultValue={Math.round(equipment.sports_mat_length / 10)} />
            </FlexRow>
            <FlexRow
              style={{
                alignItems: "center",
              }}
            >
              <span>
                <Translated keyEn="Width,&nbsp;cm" />:
              </span>
              <PDFInput defaultValue={Math.round(equipment.sports_mat_width / 10)} />
            </FlexRow>
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={equipment.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per zone" />
      </h3>
      <EquipmentZonesTableTL_Print
        equipmentImpactorWeight={10}
        equipmentUUID={equipment.uuid}
        zones={memoEquipmentZones}
      />
      {equipmentPageMode === PDFEquipmentPageMode.MultiPage && (
        <Typography.Text italic>
          <Translated keyEn="Table continued on next page..." />
        </Typography.Text>
      )}
    </FlexCol>
  )
}

const EquipmentPageContent: FC<IEquipmentPageContentProps> = (props) => {
  const site = props.site
  if (site === null) {
    return null
  }

  switch (site.norm_type) {
    case NormType.EN_1177:
      switch (site.method_type_en_1177) {
        case MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION:
          return <EquipmentPageContentPF_CFH {...props} />
        case MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE:
          return <EquipmentPageContentPF_ADQ {...props} />
      }
    case NormType.EN_12503:
      return <EquipmentPageContentSM {...props} />
    case NormType.EN_ISO_23659:
      return <EquipmentPageContentTL {...props} />
    default:
      return null
  }
}

interface IZonePageContentProps {
  user: ICloudUser | null
  site: Site
  equipment: Equipment
  zone: Zone
  zoneImpactsMap: Record<string, Impact[]> | null
}

const ZonePageContentPF_ADQ: FC<IZonePageContentProps> = ({
  user,
  site,
  equipment,
  zone,
  zoneImpactsMap,
}) => {
  const [fullImpact, getFullImpact] = useUsercommImpactBimodal()
  const { translated } = useTranslationContext()

  const [mostUnfavorableImpactChartProps, setMostUnfavorableImpactChartProps] = useState<{
    impactPoints: IImpactPoints
    impactFrequency: number
  } | null>(null)

  const memoZoneImpacts = useMemo(() => {
    if (zoneImpactsMap === null) {
      return []
    }
    let impacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
    if (impacts === undefined) {
      return []
    }
    return impacts
  }, [zoneImpactsMap])

  useEffect(() => {
    let filteredImpacts = memoZoneImpacts.filter((impact) => impact.deleted_at === 0)
    if (filteredImpacts.length === 0) {
      return
    }
    filteredImpacts.sort((a, b) => {
      return b.impact_gmax - a.impact_gmax
    })
    let _impact = filteredImpacts[0]
    if (_impact === undefined) {
      return
    }
    getFullImpact(_impact.uuid, true, true, false)
  }, [memoZoneImpacts])

  useEffect(() => {
    if (fullImpact === null) {
      return
    }
    let [aq_points, ax_points, ay_points, az_points] = decodeImpactDataPoints(fullImpact)
    // offsetImpactDataPointsInplace(fullImpact, aq_points, ax_points, ay_points, az_points) // DEPRECATED: performed on the device and/or in the cloud
    // console.log(
    //   `ZonePageContentPF_ADQ: mostUnfavorableImpact: ${pbUUIDToUuid(fullImpact.uuid)}: `,
    //   fullImpact.toObject(),
    //   aq_points,
    // )
    setMostUnfavorableImpactChartProps({
      impactPoints: {
        aq_points,
        ax_points,
        ay_points,
        az_points,
      },
      impactFrequency: fullImpact.sampling_frequency,
    })
  }, [fullImpact])

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  const memoZoneProcessed = useMemo((): IZoneProcessed_ADQ | null => {
    let nb_impacts = 0
    let heightEnv = new StatEnvelope()
    let gmaxEnv = new StatEnvelope()
    let hicEnv = new StatEnvelope()
    for (let impact of memoZoneImpacts) {
      if (impact.deleted_at > 0) {
        continue
      }
      nb_impacts++
      heightEnv.addValue(impact.impact_ffh)
      gmaxEnv.addValue(impact.impact_gmax)
      hicEnv.addValue(impact.impact_hic)
    }
    let result = getZoneResultPF_Adq(memoZoneImpacts)
    if (result === null) {
      return null
    }
    return {
      zone: zone,
      nb_impacts,
      heightEnv,
      gmaxEnv,
      hicEnv,
      result,
    }
  }, [zone, memoZoneImpacts])

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Zone" />:
          </span>
          <PDFInput defaultValue={zone.zone_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} justify="space-between" style={{ width: "100%" }}>
        <Col xs={14}>
          <h3>
            <Translated keyEn="References" />
          </h3>
          <FlexCol>
            <FlexCol style={{ gap: 0 }}>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  <Translated keyEn="Floor thickness" />:
                </span>
                <PDFInput defaultValue={zone.floor_thickness} style={{ width: "fit-content" }} />
                <span> mm</span>
              </FlexRow>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  <Translated keyEn="Specimen humidity" />:
                </span>
                <PDFInput defaultValue={zone.specimen_humidity} style={{ width: "fit-content" }} />
                <span> %</span>
              </FlexRow>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  <Translated keyEn="Specimen temperature" />:
                </span>
                <PDFInput
                  defaultValue={zone.specimen_temperature}
                  style={{ width: "fit-content" }}
                />
                <span> °C</span>
              </FlexRow>
            </FlexCol>
            {/* Result */}
            {memoZoneProcessed && (
              <FlexCol style={{ gap: 5 }}>
                <h3>
                  <Translated keyEn="Results" />
                </h3>
                <div />
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Declared FFH" />:
                  </span>
                  <span>{(zone.zone_ffh_max / 100).toFixed(2)} m</span>
                </FlexRow>
                <div>
                  <FlexRow
                    style={{
                      alignItems: "center",
                    }}
                  >
                    <span style={{ whiteSpace: "nowrap" }}>
                      <Translated keyEn="Drop FFH" />:
                    </span>
                    <span>{memoZoneProcessed.heightEnv.format(2)} m</span>
                  </FlexRow>
                  {memoZoneProcessed.nb_impacts > 1 && (
                    <i style={{ fontSize: "0.7rem" }}>
                      <Translated keyEn="Format" />: (<Translated keyEn="mean ± std" />{" "}
                      <sub>95%</sub>)<sup>[n]</sup>
                    </i>
                  )}
                </div>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Gmax" />:
                  </span>
                  <span>{memoZoneProcessed.gmaxEnv.format(0)} g</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="HIC" />:
                  </span>
                  <span>{memoZoneProcessed.hicEnv.format(0)}</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Result" />:
                  </span>
                  {
                    <EResultTagAdequacy
                      result={memoZoneProcessed.result?.result ?? EResult.ToReview}
                    />
                  }
                </FlexRow>
              </FlexCol>
            )}
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={zone.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per impact" />
      </h3>
      <ZoneImpactsTablePF_ADQ_Print impacts={memoZoneImpacts} />
      <h3>
        <Translated keyEn="Most unfavorable impact" />
      </h3>
      <Row
        gutter={[10, 0]}
        style={{ width: "100%", height: 250, marginTop: -20, marginBottom: -15 }}
      >
        <Col xs={10} style={{ height: "100%" }}>
          {mostUnfavorableImpactChartProps && (
            <ImpactChartRawAcceleration
              {...mostUnfavorableImpactChartProps}
              mooSync={null}
              showLegend={false}
            />
          )}
        </Col>
        <Col xs={7} style={{ height: "100%" }}>
          <ZoneChartPF_ADQ impacts={memoZoneImpacts} impact_key="impact_gmax" zone={zone} />
        </Col>
        <Col xs={7} style={{ height: "100%" }}>
          <ZoneChartPF_ADQ impacts={memoZoneImpacts} impact_key="impact_hic" zone={zone} />
        </Col>
      </Row>
      <PDFInputTextArea value={zone.comments} autoSize />
      <PDFInputTextArea
        style={{
          textAlign: "center",
          fontSize: "0.8rem",
        }}
        autoSize={{ minRows: 2 }}
        defaultValue={memoMustBeTranslated(
          "The uncertainty of this result under controlled laboratory conditions is ± 7%. Under site conditions, it is possible that the uncertainty is greater.",
        )}
      />
    </FlexCol>
  )
}

const ZonePageContentPF_CFH: FC<IZonePageContentProps> = ({
  user,
  site,
  equipment,
  zone,
  zoneImpactsMap,
}) => {
  const [fullImpact, getFullImpact] = useUsercommImpactBimodal()
  const { translated } = useTranslationContext()

  const [mostUnfavorableImpactChartProps, setMostUnfavorableImpactChartProps] = useState<{
    impactPoints: IImpactPoints
    impactFrequency: number
  } | null>(null)

  const memoZoneImpacts = useMemo(() => {
    if (zoneImpactsMap === null) {
      return []
    }
    let impacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
    if (impacts === undefined) {
      return []
    }
    return impacts
  }, [zoneImpactsMap])

  useEffect(() => {
    let filteredImpacts = memoZoneImpacts.filter((impact) => impact.deleted_at === 0)
    if (filteredImpacts.length === 0) {
      return
    }
    filteredImpacts.sort((a, b) => {
      return b.impact_gmax - a.impact_gmax
    })
    let _impact = filteredImpacts[0]
    if (_impact === undefined) {
      return
    }
    getFullImpact(_impact.uuid, true, true, false)
  }, [memoZoneImpacts])

  useEffect(() => {
    if (fullImpact === null) {
      return
    }
    let [aq_points, ax_points, ay_points, az_points] = decodeImpactDataPoints(fullImpact)
    // offsetImpactDataPointsInplace(fullImpact, aq_points, ax_points, ay_points, az_points) // DEPRECATED: performed on the device and/or in the cloud
    // console.log(
    //   `ZonePageContentPF_CFH: mostUnfavorableImpact: ${pbUUIDToUuid(fullImpact.uuid)}: `,
    //   fullImpact.toObject(),
    //   aq_points,
    // )
    setMostUnfavorableImpactChartProps({
      impactPoints: {
        aq_points,
        ax_points,
        ay_points,
        az_points,
      },
      impactFrequency: fullImpact.sampling_frequency,
    })
  }, [fullImpact])

  const memoZoneProcessed = useMemo((): IZoneProcessed_CFH | null => {
    if (memoZoneImpacts.length === 0) {
      return null
    }
    let nb_impacts = 0
    for (let impact of memoZoneImpacts) {
      if (impact.deleted_at === 0) {
        nb_impacts++
      }
    }
    let cfh = getCFH_Global(memoZoneImpacts)
    if (cfh === null) {
      return null
    }
    let result = getZoneResultPF_CFH(zone, memoZoneImpacts)
    if (result === null) {
      return null
    }
    return {
      zone: zone,
      nb_impacts,
      cfh,
      result,
    }
  }, [zone, memoZoneImpacts])

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Zone" />:
          </span>
          <PDFInput defaultValue={zone.zone_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} justify="space-between" style={{ width: "100%" }}>
        <Col xs={14}>
          <h3>
            <Translated keyEn="References" />
          </h3>
          <FlexCol>
            <FlexCol style={{ gap: 0 }}>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  <Translated keyEn="Floor thickness" />:
                </span>
                <PDFInput defaultValue={zone.floor_thickness} style={{ width: "fit-content" }} />
                <span> mm</span>
              </FlexRow>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  <Translated keyEn="Specimen humidity" />:
                </span>
                <PDFInput defaultValue={zone.specimen_humidity} style={{ width: "fit-content" }} />
                <span> %</span>
              </FlexRow>
              <FlexRow
                style={{
                  alignItems: "center",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  <Translated keyEn="Specimen temperature" />:
                </span>
                <PDFInput
                  defaultValue={zone.specimen_temperature}
                  style={{ width: "fit-content" }}
                />
                <span> °C</span>
              </FlexRow>
            </FlexCol>
            {/* Result */}
            {memoZoneProcessed && (
              <FlexCol style={{ gap: 5 }}>
                <div>
                  <h3>
                    <Translated keyEn="Results" />
                  </h3>
                </div>
                <div />
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Declared FFH" />:
                  </span>
                  <span>{(zone.zone_ffh_max / 100).toFixed(2)} m</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="CFH" />:
                  </span>
                  <span>{memoZoneProcessed.cfh?.mean.toFixed(2)} m</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Result" />:
                  </span>
                  {
                    <EResultTagAdequacy
                      result={memoZoneProcessed.result?.result ?? EResult.ToReview}
                    />
                  }
                </FlexRow>
              </FlexCol>
            )}
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={zone.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per impact" />
      </h3>
      <ZoneImpactsTablePF_CFH_Print impacts={memoZoneImpacts} />
      <h3>
        <Translated keyEn="Data" />
      </h3>
      <Row
        gutter={[10, 0]}
        style={{ width: "100%", height: 250, marginTop: -20, marginBottom: -15 }}
      >
        <Col xs={10} style={{ height: "100%" }}>
          {mostUnfavorableImpactChartProps && (
            <ImpactChartRawAcceleration
              {...mostUnfavorableImpactChartProps}
              mooSync={null}
              showLegend={false}
            />
          )}
        </Col>
        <Col xs={14} style={{ height: "100%" }}>
          <ZoneChartPF_CFH impacts={memoZoneImpacts} zone={zone} showLegend={false} />
        </Col>
      </Row>
      <PDFInputTextArea value={zone.comments} autoSize />
      <PDFInputTextArea
        style={{
          textAlign: "center",
          fontSize: "0.8rem",
        }}
        autoSize={{ minRows: 2 }}
        defaultValue={memoMustBeTranslated(
          "The uncertainty of this result under controlled laboratory conditions is ± 7%. Under site conditions, it is possible that the uncertainty is greater.",
        )}
      />
    </FlexCol>
  )
}

const ZonePageContentSM: FC<IZonePageContentProps> = ({
  user,
  site,
  equipment,
  zone,
  zoneImpactsMap,
}) => {
  const [fullImpact, getFullImpact] = useUsercommImpactBimodal()
  const { translated } = useTranslationContext()

  const memoSportsMatThicknessMeanAndStd = useMemo((): MeanWithStd | null => {
    return getEquipmentSportsMatThicknessMeanAndStd([
      equipment.sports_mat_thickness_side_one,
      equipment.sports_mat_thickness_side_two,
      equipment.sports_mat_thickness_side_three,
      equipment.sports_mat_thickness_side_four,
    ])
  }, [equipment])

  const memoZoneImpacts = useMemo(() => {
    if (zoneImpactsMap === null) {
      return []
    }
    let impacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
    if (impacts === undefined) {
      return []
    }
    return impacts
  }, [zoneImpactsMap])

  useEffect(() => {
    let filteredImpacts = memoZoneImpacts.filter((impact) => impact.deleted_at === 0)
    if (filteredImpacts.length === 0) {
      return
    }
    filteredImpacts.sort((a, b) => {
      return b.impact_gmax - a.impact_gmax
    })
    let _impact = filteredImpacts[0]
    if (_impact === undefined) {
      return
    }
    getFullImpact(_impact.uuid, true, true, false)
  }, [memoZoneImpacts])

  const memoZoneProcessed = useMemo((): IZoneProcessed_SM | null => {
    if (memoZoneImpacts.length === 0) {
      return null
    }
    let nb_impacts = 0
    let ffhEnv = new StatEnvelope()
    for (let impact of memoZoneImpacts) {
      if (impact.deleted_at === 0) {
        nb_impacts++
        ffhEnv.addValue(impact.impact_ffh)
      }
    }
    let smType = equipment.sports_mat_type
    let smThickness = memoSportsMatThicknessMeanAndStd?.mean ?? null
    let result = getZoneResultSM(memoZoneImpacts, smType, smThickness)
    if (result === null) {
      return null
    }
    return {
      zone: zone,
      nb_impacts,
      ffhEnv,
      gmaxMstd: result.gmax,
      deflectionDistanceMMMstd: result.deflectionDistanceMM,
      deflectionDistancePercMstd: result.deflectionDistancePerc,
      resiliencePercMstd: result.resiliencePerc,
      result,
    }
  }, [zone, memoZoneImpacts, memoSportsMatThicknessMeanAndStd])

  const memoImpactCharts_SM = useMemo(() => {
    if (fullImpact === null) {
      return null
    }
    let [aq_points, ax_points, ay_points, az_points] = decodeImpactDataPoints(fullImpact)
    // offsetImpactDataPointsInplace(fullImpact, aq_points, ax_points, ay_points, az_points) // DEPRECATED: performed on the device and/or in the cloud

    let impactKinematics = getImpactKinematics(fullImpact, aq_points)
    if (impactKinematics === null) {
      return null
    }
    let impactSamplingFrequency = fullImpact.sampling_frequency
    let impactDeflectionDistanceMM = impactKinematics.deflectionDistanceMM
    let impactDeflectionTimeMS = impactKinematics.deflectionTimeMS
    let impactKinematicPoints = impactKinematics.kinematicPoints
    let chartColStyle = { height: 300 }
    let requirements = getRequirementsSM(equipment.sports_mat_type)
    let totalDepth = getEquipmentSportsMatThicknessMeanAndStd([
      equipment.sports_mat_thickness_side_one,
      equipment.sports_mat_thickness_side_two,
      equipment.sports_mat_thickness_side_three,
      equipment.sports_mat_thickness_side_four,
    ])
    return (
      <Row justify="center">
        <Col xs={8} style={chartColStyle}>
          <ImpactChartKinematicAcceleration
            kinematicPoints={impactKinematicPoints}
            maxAcceptableG={requirements === null ? null : requirements.gmax}
            mooSync={null}
            shouldDrawRequirements={true}
            shouldShowLegend={false}
          />
        </Col>
        <Col xs={8} style={chartColStyle}>
          <ImpactChartKinematicVelocity_Resilience
            impactKinematics={impactKinematics}
            maxAcceptableResiliencePerc={
              requirements !== null && requirements.resiliencePerc !== null
                ? requirements.resiliencePerc[1]
                : null
            }
            mooSync={null}
            shouldDrawRequirements={true}
            shouldShowLegend={false}
          />
        </Col>
        <Col xs={8} style={chartColStyle}>
          <ImpactChartKinematicDistance_Deflection
            kinematicPoints={impactKinematicPoints}
            deflectionDistanceMM={impactDeflectionDistanceMM}
            deflectionTimeMs={impactDeflectionTimeMS}
            totalDepth={totalDepth}
            maxAcceptableDeformationDist={
              requirements === null ? null : requirements.deformationDistanceMM
            }
            maxAcceptableDeformationPerc={
              requirements === null ? null : requirements.deformationDistancePerc
            }
            mooSync={null}
            shouldDrawRequirements={true}
            shouldShowLegend={false}
          />
        </Col>
      </Row>
    )
  }, [fullImpact])

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Zone" />:
          </span>
          <PDFInput defaultValue={zone.zone_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} justify="space-between" style={{ width: "100%" }}>
        <Col xs={14}>
          <FlexCol>
            {/* Result */}
            {memoZoneProcessed && (
              <FlexCol style={{ gap: 5 }}>
                <div>
                  <h3>
                    <Translated keyEn="Results" />
                  </h3>
                </div>
                <div />
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Gmax" />:
                  </span>
                  {memoZoneProcessed.gmaxMstd!.mean.toFixed(1)} g
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Deflection" />:
                  </span>
                  {memoZoneProcessed.deflectionDistanceMMMstd!.mean.toFixed(1)} mm
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Deflection" />:
                  </span>
                  {memoZoneProcessed.deflectionDistancePercMstd!.mean.toFixed(1)} %
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Translated keyEn="Resilience" />:
                  </span>
                  {memoZoneProcessed.resiliencePercMstd!.mean.toFixed(1)} %
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Result" />:
                  </span>
                  {
                    <EResultTagAdequacy
                      result={memoZoneProcessed.result?.result ?? EResult.ToReview}
                    />
                  }
                </FlexRow>
              </FlexCol>
            )}
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={zone.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per impact" />
      </h3>
      <ZoneImpactsTableSM_Print impacts={memoZoneImpacts} equipment={equipment} />
      <h3>
        <Translated keyEn="Data" />
      </h3>
      <Row style={{ width: "100%", marginTop: -10, marginBottom: -15 }}>
        <Col xs={24} style={{ height: "100%" }}>
          {memoImpactCharts_SM}
        </Col>
      </Row>
      <PDFInputTextArea value={zone.comments} autoSize />
      <PDFInputTextArea
        style={{
          textAlign: "center",
          fontSize: "0.8rem",
        }}
        autoSize={{ minRows: 2 }}
        defaultValue={memoMustBeTranslated(
          "The uncertainty of this result under controlled laboratory conditions is ± 7%. Under site conditions, it is possible that the uncertainty is greater.",
        )}
      />
    </FlexCol>
  )
}

const ZonePageContentTL: FC<IZonePageContentProps> = ({
  user,
  site,
  equipment,
  zone,
  zoneImpactsMap,
}) => {
  const [fullImpact, getFullImpact] = useUsercommImpactBimodal()

  const memoZoneImpacts = useMemo(() => {
    if (zoneImpactsMap === null) {
      return []
    }
    let impacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
    if (impacts === undefined) {
      return []
    }
    return impacts
  }, [zoneImpactsMap])

  useEffect(() => {
    let filteredImpacts = memoZoneImpacts.filter((impact) => impact.deleted_at === 0)
    if (filteredImpacts.length === 0) {
      return
    }
    let filteredImpactsWithPF: { impact: Impact; pf: number }[] = []
    for (let impact of filteredImpacts) {
      let pf = _calculatePerformanceFactor(impact.impact_rebounce_height, impact.impact_gmax)
      filteredImpactsWithPF.push({ impact, pf })
    }
    filteredImpactsWithPF.sort((a, b) => {
      return b.pf - a.pf
    })
    let _impact = filteredImpactsWithPF[0]?.impact
    if (_impact === undefined) {
      return
    }
    getFullImpact(_impact.uuid, true, true, false)
  }, [memoZoneImpacts])

  const memoZoneProcessed = useMemo((): IZoneProcessed_TL | null => {
    if (memoZoneImpacts.length === 0) {
      return null
    }
    let nb_impacts = 0
    let ffhEnv = new StatEnvelope()
    let gmaxEnv = new StatEnvelope()
    let rebounceHeightEnv = new StatEnvelope()
    let performanceFactorEnv = new StatEnvelope()
    for (let impact of memoZoneImpacts) {
      if (impact.deleted_at === 0) {
        nb_impacts++
        ffhEnv.addValue(impact.impact_ffh)
        gmaxEnv.addValue(impact.impact_gmax)
        rebounceHeightEnv.addValue(impact.impact_rebounce_height)
        performanceFactorEnv.addValue(
          _calculatePerformanceFactor(impact.impact_rebounce_height, impact.impact_gmax),
        )
      }
    }
    let result = getZoneResultTL(memoZoneImpacts)
    if (result === null) {
      return null
    }
    return {
      zone: zone,
      ffhEnv,
      gmaxEnv,
      rebounceHeightEnv,
      performanceFactorEnv,
      result,
    }
  }, [zone, memoZoneImpacts])

  const memoImpactCharts_TL = useMemo(() => {
    if (fullImpact === null) {
      return null
    }
    let [aq_points, ax_points, ay_points, az_points] = decodeImpactDataPoints(fullImpact)
    // offsetImpactDataPointsInplace(fullImpact, aq_points, ax_points, ay_points, az_points) // DEPRECATED: performed on the device and/or in the cloud

    let impactKinematics = getImpactKinematics(fullImpact, aq_points)
    if (impactKinematics === null) {
      return null
    }
    let impactKinematicPoints = impactKinematics.kinematicPoints
    let impactDeflectionDistanceMM = impactKinematics.deflectionDistanceMM
    let impactDeflectionTimeMS = impactKinematics.deflectionTimeMS
    let impactSecondarySuspenseHeightM = impactKinematics.secondarySuspenseHeightM
    let impactSecondarySuspenseTimeMS = impactKinematics.secondarySuspenseTimeMS
    let chartColStyle = { height: 300 }
    return (
      <Row justify="center">
        <Col xs={8} style={chartColStyle}>
          <ImpactChartKinematicAcceleration
            kinematicPoints={impactKinematicPoints}
            maxAcceptableG={null}
            mooSync={null}
            shouldDrawRequirements={false}
            shouldShowLegend={false}
          />
        </Col>
        <Col xs={8} style={chartColStyle}>
          <ImpactChartKinematicVelocity_SecondarySuspense
            kinematicPoints={impactKinematicPoints}
            mooSync={null}
            shouldShowLegend={false}
          />
        </Col>
        <Col xs={8} style={chartColStyle}>
          <ImpactChartKinematicDistance_SecondarySuspense
            kinematicPoints={impactKinematicPoints}
            deflectionDistanceMM={impactDeflectionDistanceMM}
            deflectionTimeMs={impactDeflectionTimeMS}
            secondarySuspenseHeightM={impactSecondarySuspenseHeightM}
            secondarySuspenseTimeMS={impactSecondarySuspenseTimeMS}
            mooSync={null}
            shouldShowLegend={false}
          />
        </Col>
      </Row>
    )
  }, [fullImpact])

  return (
    <FlexCol
      style={{
        justifyContent: "start",
        alignItems: "start",
        maxHeight: "90%",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          width: "100%",
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>
            <Translated keyEn="Zone" />:
          </span>
          <PDFInput defaultValue={zone.zone_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} justify="space-between" style={{ width: "100%" }}>
        <Col xs={14}>
          <FlexCol>
            {/* Result */}
            {memoZoneProcessed && (
              <FlexCol style={{ gap: 5 }}>
                <div>
                  <h3>
                    <Translated keyEn="Results" />
                  </h3>
                </div>
                <div />
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Target FFH" />:
                  </span>
                  <span>1.00 ± 0.01 m</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Drop FFH" />:
                  </span>
                  <span>{memoZoneProcessed.ffhEnv.format(2)} m</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Rebound height" />:
                  </span>
                  <span>{memoZoneProcessed.rebounceHeightEnv.format(2)} m</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Gmax" />:
                  </span>
                  <span>{memoZoneProcessed.gmaxEnv.format(1, 2)} g</span>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <b style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Performance factor" />:
                  </b>
                  <b>{memoZoneProcessed.performanceFactorEnv.format(0, 0)}</b>
                </FlexRow>
                <FlexRow
                  style={{
                    alignItems: "center",
                  }}
                >
                  <b style={{ whiteSpace: "nowrap" }}>
                    <Translated keyEn="Result" />:
                  </b>
                  {<EResultTagTL result={memoZoneProcessed.result?.result ?? EResult.ToReview} />}
                </FlexRow>
              </FlexCol>
            )}
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={zone.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Results per impact" />
      </h3>
      <ZoneImpactsTableTL_Print
        impacts={memoZoneImpacts}
        equipmentImpactorWeight={equipment.trampoline_impactor_weight}
      />
      <Row style={{ width: "100%", marginTop: -10, marginBottom: -15 }}>
        <Col xs={24} style={{ height: "100%" }}>
          {memoImpactCharts_TL}
        </Col>
      </Row>
      <PDFInputTextArea value={zone.comments} autoSize />
    </FlexCol>
  )
}

const ZonePageContent: FC<IZonePageContentProps> = (props) => {
  const site = props.site
  if (site === null) {
    return null
  }
  switch (site.norm_type) {
    case NormType.EN_1177:
      switch (site.method_type_en_1177) {
        case MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION:
          return <ZonePageContentPF_CFH {...props} />
        case MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE:
          return <ZonePageContentPF_ADQ {...props} />
      }
    case NormType.EN_12503:
      return <ZonePageContentSM {...props} />
    case NormType.EN_ISO_23659:
      return <ZonePageContentTL {...props} />
  }
  return null
}

const LastPageContent: FC<{
  user: ICloudUser | null
  site: Site
}> = ({ user, site }) => {
  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        alignItems: "center",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      <PDFInputTextArea defaultValue={"   "} autoSize={{ minRows: 2 }} />
      <PDFEmptySpacePlaceholder />
    </FlexCol>
  )
}

export const ReportMasterPage: FC = () => {
  const location = useLocation()

  const siteUUID = useMemo((): UUID | null => {
    let siteUUID = parsePathForEntityUUID(location.pathname)
    if (siteUUID === null) {
      return null
    }
    return uuidToPbUUID(siteUUID)
  }, [location])

  const [totalPages, setTotalPages] = useState(2)
  const [site, getSite] = useUsercommSiteBimodal()
  const [siteEquipmentsMap, equipmentZonesMap, zoneImpactsMap, getSiteChildrenRecursive] =
    useUsercommSiteChildrenRecursiveBimodal()
  const [siteAnnexes, getSiteAnnexes] = useUsercommSiteAnnexesBimodal()
  const [user, setUser] = useState<ICloudUser | null>(null)
  const [device, setDevice] = useState<ICloudDevice | null>(null)
  const [deviceEvents, setDeviceEvents] = useState<ICloudDeviceEvent[] | null>(null)
  const [enterpriseCustomers, setEnterpriseCustomers] = useState<ICloudCustomer[] | null>(null)

  const [primaryPagesMode, setPrimaryPagesMode] = useState<PDFPrimaryPagesMode>(
    PDFPrimaryPagesMode.CompactMode,
  )

  // User
  useEffect(() => {
    if (site === null || !site.user_uuid) {
      return
    }
    callCloudApiV2<ICloudUser | null>(`/user?user_uuid=${pbUUIDToUuid(site.user_uuid)}`).then(
      ({ resp, entity: _user }) => {
        if (!resp.ok || _user === null) {
          console.error(`ReportPage: error fetching user`, resp)
        }
        console.log(`ReportPage: user`, _user)
        setUser(_user)
      },
    )
  }, [site])

  // Device
  useEffect(() => {
    if (site === null) {
      return
    }
    console.log(`ReportPage: site`, site.toObject())
    let deviceSnStr = site.device_sn

    if (deviceSnStr === "" || deviceSnStr === "0") {
      // try to figure out serial number from impacts
      if (zoneImpactsMap === null) {
        return
      }
      for (let zoneUUID in zoneImpactsMap) {
        let impacts = zoneImpactsMap[zoneUUID]
        if (impacts === undefined) {
          continue
        }
        for (let impact of impacts) {
          if (impact.metadata_station_device_sn !== "") {
            deviceSnStr = impact.metadata_station_device_sn
            console.log(
              `ReportPage: retreived deviceSnStr from impact metadata`,
              deviceSnStr,
              impact.toObject(),
            )
            break
          }
        }
        if (deviceSnStr !== "") {
          break
        }
      }
      if (deviceSnStr === "") {
        return
      }
    }

    let deviceSnBigInt = BigInt(deviceSnStr)
    if (deviceSnBigInt !== BigInt(0)) {
      deviceSnStr = deviceSnBigInt.toString(16)
    }

    callCloudApiV2<ICloudDevice | null>(`/device?device_serial_number=${deviceSnStr}`).then(
      ({ resp, entity: _device }) => {
        if (!resp.ok || _device === null) {
          console.error(`ReportPage: error fetching device`, resp)
        }
        // console.log(`ReportPage: device`, _device)
        setDevice(_device)
      },
    )
  }, [site, zoneImpactsMap])

  // Device Events
  useEffect(() => {
    if (device === null) {
      return
    }
    callCloudApiV2<ICloudDeviceEvent[]>(`/device/events?device_uuid=${device.UUID}`).then(
      ({ resp, entity: _deviceEvents }) => {
        if (!resp.ok || _deviceEvents === null) {
          console.error(`ReportPage: error fetching device events`, resp)
        }
        console.log(`ReportPage: device events`, _deviceEvents)
        setDeviceEvents(_deviceEvents)
      },
    )
  }, [device])

  // Enterprise Customers
  useEffect(() => {
    if (user === null || user.Enterprise === null) {
      return
    }
    callCloudApiV2<ICloudCustomer[]>(
      `/enterprise/customers?enterprise_uuid=${user.Enterprise.UUID}`,
    ).then(({ resp, entity: _enterpriseCustomers }) => {
      if (!resp.ok || _enterpriseCustomers === null) {
        console.error(`ReportPage: error fetching enterprise customers`, resp)
      }
      console.log(`ReportPage: enterprise customers`, _enterpriseCustomers)
      setEnterpriseCustomers(_enterpriseCustomers)
    })
  }, [user?.Enterprise])

  // Site
  useEffect(() => {
    if (siteUUID === null) {
      return
    }
    getSite(siteUUID)
  }, [siteUUID])

  // Site children
  useEffect(() => {
    if (site == null) {
      return
    }
    getSiteChildrenRecursive(site.uuid)
    getSiteAnnexes(site.uuid)
  }, [site])

  useEffect(() => {
    if (
      siteEquipmentsMap === null ||
      equipmentZonesMap === null ||
      siteAnnexes === null ||
      zoneImpactsMap === null
    ) {
      return
    }
    let nbPages = 3
    if (
      primaryPagesMode === PDFPrimaryPagesMode.ExpandedMode ||
      primaryPagesMode === PDFPrimaryPagesMode.SuperExpandedMode
    ) {
      nbPages++
    }
    for (let siteUUID in siteEquipmentsMap) {
      let equipments = siteEquipmentsMap[siteUUID]
      if (equipments === undefined) {
        continue
      }
      for (let equipment of equipments) {
        if (equipment.deleted_at > 0) {
          continue
        }
        nbPages++
        let equipmentZones = equipmentZonesMap[pbUUIDToUuid(equipment.uuid)]
        if (equipmentZones === undefined) {
          continue
        }
        if (equipmentZones.length > PDF_EQUIPMENT_ZONES_MAX_SINGLE_PAGE_COUNT) {
          // MultiPage Mode for equipment zones
          nbPages++
        }
        for (let zone of equipmentZones) {
          if (zone.deleted_at > 0) {
            continue
          }
          let impacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
          if (impacts === undefined || impacts.length === 0) {
            continue
          }
          nbPages++
        }
      }
    }
    for (let annex of siteAnnexes) {
      if (annex.deleted_at > 0) {
        continue
      }
      nbPages++
    }
    setTotalPages(nbPages)
  }, [siteEquipmentsMap, equipmentZonesMap, siteAnnexes, zoneImpactsMap, primaryPagesMode])

  // Primary Pages Mode
  useEffect(() => {
    if (siteEquipmentsMap === null || site === null) {
      return
    }
    let equipments = siteEquipmentsMap[pbUUIDToUuid(site.uuid)]
    if (equipments === undefined) {
      return
    }
    if (equipments.length <= PDF_EQUIPMENT_SUMMARY_EXPANDED_MODE_COUNT) {
      setPrimaryPagesMode(PDFPrimaryPagesMode.CompactMode)
    } else if (
      equipments.length > PDF_EQUIPMENT_SUMMARY_EXPANDED_MODE_COUNT &&
      equipments.length <= PDF_EQUIPMENT_SUMMARY_SUPEREXPANDED_MODE_COUNT
    ) {
      setPrimaryPagesMode(PDFPrimaryPagesMode.ExpandedMode)
    } else {
      setPrimaryPagesMode(PDFPrimaryPagesMode.SuperExpandedMode)
    }
  }, [site, siteEquipmentsMap])

  const memoGlobalResult = useMemo(() => {
    if (
      site === null ||
      siteEquipmentsMap === null ||
      equipmentZonesMap === null ||
      zoneImpactsMap === null
    ) {
      return null
    }
    let siteEquipments = siteEquipmentsMap[pbUUIDToUuid(site.uuid)]
    if (siteEquipments === undefined) {
      return null
    }
    switch (site.norm_type) {
      case NormType.EN_1177:
        switch (site.method_type_en_1177) {
          case MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION:
            return getSiteResultPF_CFH(siteEquipments, equipmentZonesMap, zoneImpactsMap)
          case MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE:
            return getSiteResultPF_Adq(siteEquipments, equipmentZonesMap, zoneImpactsMap)
        }
      case NormType.EN_12503:
        return getSiteResultSM(siteEquipments, equipmentZonesMap, zoneImpactsMap)
      case NormType.EN_ISO_23659:
        return getSiteResultTL(siteEquipments, equipmentZonesMap, zoneImpactsMap)
    }
    return null
  }, [site, siteEquipmentsMap, equipmentZonesMap, zoneImpactsMap])

  const memoEquipmentSummaryPages = useMemo(() => {
    if (site === null || siteEquipmentsMap === null || equipmentZonesMap === null) {
      return <></>
    }
    let equipments = siteEquipmentsMap[pbUUIDToUuid(site.uuid)]
    if (equipments === undefined || equipments.length === 0) {
      return <></>
    }
    equipments = equipments.filter((equipment) => equipment.deleted_at === 0)

    if (primaryPagesMode === PDFPrimaryPagesMode.CompactMode) {
      return (
        <PDFPage site={site} user={user} pageNumber={3} totalPages={totalPages}>
          <ThirdPageContent
            user={user}
            device={device}
            deviceEvents={deviceEvents}
            site={site}
            siteEquipmentsMap={siteEquipmentsMap}
            equipmentZonesMap={equipmentZonesMap}
            zoneImpactsMap={zoneImpactsMap}
            primaryPageMode={primaryPagesMode}
          />
        </PDFPage>
      )
    } else {
      return (
        <>
          <PDFPage site={site} user={user} pageNumber={3} totalPages={totalPages}>
            <ThirdPageContent
              user={user}
              device={device}
              deviceEvents={deviceEvents}
              site={site}
              siteEquipmentsMap={siteEquipmentsMap}
              equipmentZonesMap={equipmentZonesMap}
              zoneImpactsMap={zoneImpactsMap}
              primaryPageMode={primaryPagesMode}
            />
          </PDFPage>
          <PDFPage site={site} user={user} pageNumber={4} totalPages={totalPages}>
            <FourthPageContent
              site={site}
              siteEquipmentsMap={siteEquipmentsMap}
              equipmentZonesMap={equipmentZonesMap}
              zoneImpactsMap={zoneImpactsMap}
              primaryPageMode={primaryPagesMode}
            />
          </PDFPage>
        </>
      )
    }
  }, [
    siteEquipmentsMap,
    equipmentZonesMap,
    zoneImpactsMap,
    totalPages,
    user,
    site,
    device,
    deviceEvents,
    primaryPagesMode,
  ])

  const memoDataPages = useMemo(() => {
    if (
      site === null ||
      siteEquipmentsMap === null ||
      equipmentZonesMap === null ||
      zoneImpactsMap === null ||
      siteAnnexes === null
    ) {
      return <></>
    }
    let nbPrimaryPagesBeforeContent = 3
    if (primaryPagesMode === PDFPrimaryPagesMode.ExpandedMode) {
      nbPrimaryPagesBeforeContent++
    }
    let i = 0
    let pages: JSX.Element[] = []

    for (let siteUUID in siteEquipmentsMap) {
      let equipments = siteEquipmentsMap[siteUUID]
      if (equipments === undefined) {
        continue
      }
      equipments = equipments.filter((equipment) => equipment.deleted_at === 0)

      // sort equipments by equipment_name
      equipments.sort((a, b) => {
        return a.equipment_name.localeCompare(b.equipment_name)
      })
      for (let equipment of equipments) {
        if (equipment.deleted_at > 0) {
          continue
        }
        let equipmentUUID = pbUUIDToUuid(equipment.uuid)
        let zones = equipmentZonesMap[equipmentUUID]
        if (zones === undefined) {
          continue
        }
        i++
        if (zones.length <= PDF_EQUIPMENT_ZONES_MAX_SINGLE_PAGE_COUNT) {
          let primaryEquipmentPage = (
            <PDFPage
              site={site}
              user={user}
              pageNumber={nbPrimaryPagesBeforeContent + i}
              totalPages={totalPages}
              key={pbUUIDToUuid(equipment.uuid) + "_primary"}
            >
              <EquipmentPageContent
                user={user}
                site={site}
                equipment={equipment}
                equipmentZonesMap={{ [equipmentUUID]: zones }}
                zoneImpactsMap={zoneImpactsMap}
                equipmentPageMode={PDFEquipmentPageMode.SinglePage}
                multiPageIndex={0}
              />
            </PDFPage>
          )
          pages.push(primaryEquipmentPage)
        } else {
          zones.sort((a, b) => {
            return a.zone_name.localeCompare(b.zone_name)
          })
          let _sliceIdx = PDF_EQUIPMENT_ZONES_MAX_SINGLE_PAGE_COUNT
          if (zones.length - PDF_EQUIPMENT_ZONES_MAX_SINGLE_PAGE_COUNT < 3) {
            _sliceIdx = zones.length - 3
          }
          let primaryEquipmentPage = (
            <PDFPage
              site={site}
              user={user}
              pageNumber={nbPrimaryPagesBeforeContent + i}
              totalPages={totalPages}
              key={pbUUIDToUuid(equipment.uuid) + "_primary"}
            >
              <EquipmentPageContent
                user={user}
                site={site}
                equipment={equipment}
                equipmentZonesMap={{
                  [equipmentUUID]: zones.slice(0, _sliceIdx),
                }}
                zoneImpactsMap={zoneImpactsMap}
                equipmentPageMode={PDFEquipmentPageMode.MultiPage}
                multiPageIndex={0}
              />
            </PDFPage>
          )
          pages.push(primaryEquipmentPage)
          i++
          let secondaryEquipmentPage = (
            <PDFPage
              site={site}
              user={user}
              pageNumber={nbPrimaryPagesBeforeContent + i}
              totalPages={totalPages}
              key={pbUUIDToUuid(equipment.uuid) + "_secondary"}
            >
              <EquipmentPageContent
                user={user}
                site={site}
                equipment={equipment}
                equipmentZonesMap={{
                  [equipmentUUID]: zones.slice(_sliceIdx),
                }}
                zoneImpactsMap={zoneImpactsMap}
                equipmentPageMode={PDFEquipmentPageMode.MultiPage}
                multiPageIndex={1}
              />
            </PDFPage>
          )
          pages.push(secondaryEquipmentPage)
        }
        let equipmentZones = equipmentZonesMap[pbUUIDToUuid(equipment.uuid)]
        if (equipmentZones === undefined) {
          continue
        }
        // sort zones by zone_name
        equipmentZones.sort((a, b) => {
          return a.zone_name.localeCompare(b.zone_name)
        })
        for (let zone of equipmentZones) {
          if (zone.deleted_at > 0) {
            continue
          }
          let impacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
          if (impacts === undefined || impacts.length === 0) {
            continue
          }
          i++
          let equipmentZonePage = (
            <PDFPage
              site={site}
              user={user}
              pageNumber={nbPrimaryPagesBeforeContent + i}
              totalPages={totalPages}
              key={pbUUIDToUuid(zone.uuid)}
            >
              <ZonePageContent
                user={user}
                site={site}
                equipment={equipment}
                zone={zone}
                zoneImpactsMap={zoneImpactsMap}
              />
            </PDFPage>
          )
          pages.push(equipmentZonePage)
          //   TEMPORARY BREAK
          //   break
        }
      }
    }

    let filteredSortedSiteAnnexes = siteAnnexes.filter((annex) => annex.deleted_at === 0)
    filteredSortedSiteAnnexes.sort((a, b) => {
      return a.annex_idx - b.annex_idx
    })
    for (let j = 0; j < filteredSortedSiteAnnexes.length; j += 2) {
      let _annexes = filteredSortedSiteAnnexes.slice(j, j + 2)
      i++
      let annexPage = (
        <PDFPage
          site={site}
          user={user}
          pageNumber={nbPrimaryPagesBeforeContent + i}
          totalPages={totalPages}
          key={Math.random()}
        >
          <AnnexPageContent user={user} site={site} annexes={_annexes} />
        </PDFPage>
      )
      pages.push(annexPage)
    }
    return pages
  }, [
    siteEquipmentsMap,
    equipmentZonesMap,
    zoneImpactsMap,
    totalPages,
    user,
    site,
    primaryPagesMode,
  ])

  if (
    site === null ||
    user === null ||
    device === null ||
    siteEquipmentsMap === null ||
    equipmentZonesMap === null ||
    zoneImpactsMap === null
  ) {
    return (
      <FlexCol
        style={{
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </FlexCol>
    )
  }

  return (
    <ReportConfigProvider>
      <div id="pdf" className="A4">
        <canvas id="text-measurement-canvas" style={{ display: "none" }} />
        <PDFPage site={site} user={user} pageNumber={1} totalPages={totalPages}>
          <FirstPageContent
            user={user}
            site={site}
            globalResult={memoGlobalResult}
            enterpriseCustomers={enterpriseCustomers}
          />
        </PDFPage>
        <PDFPage site={site} user={user} pageNumber={2} totalPages={totalPages}>
          <SecondPageContent
            user={user}
            device={device}
            deviceEvents={deviceEvents}
            site={site}
            siteEquipmentsMap={siteEquipmentsMap}
            equipmentZonesMap={equipmentZonesMap}
            zoneImpactsMap={zoneImpactsMap}
            primaryPageMode={primaryPagesMode}
          />
        </PDFPage>
        {memoEquipmentSummaryPages}
        {memoDataPages}
        {/* <PDFPage site={site} user={user} pageNumber={totalPages} totalPages={totalPages}>
          <LastPageContent user={user} site={site} />
        </PDFPage> */}
      </div>
      <div className="pdf-invisible">
        <Popover content={<TranslationLanguageSelectWidget />}>
          <FloatButton
            icon={<GlobalOutlined />}
            description={<Translated keyEn="Lang" />}
            type="default"
            style={{
              width: 60,
              height: 60,
              right: 180,
            }}
          />
        </Popover>
        <Link to="/">
          <FloatButton
            icon={<LeftCircleOutlined />}
            description={<Translated keyEn="Back" />}
            type="default"
            style={{
              width: 60,
              height: 60,
              right: 110,
            }}
          />
        </Link>
        <FloatButton
          icon={<FilePdfOutlined />}
          description={<Translated keyEn="Print" />}
          type="primary"
          style={{
            width: 60,
            height: 60,
            right: 40,
          }}
          onClick={() => {
            window.print()
          }}
        />
      </div>
    </ReportConfigProvider>
  )
}
